import {isInlineShopifyReturn} from '@/flows/shopifyOnboardingInstall';
import OnboardingShopifyInstall from '@/pages/ShopifyInstall/OnboardingShopifyInstall';
import ShopifyConnectionEntry from '@/components/ShopifyConnectionEntry';
import { completeShopifyInstallWorkspaceHandoff } from '@/flows/shopifyInstall';
import { buildOnboardingTrialProfile } from "@/components/Pricing/onboardingTrialProfile";
import wordmark from "@/assets/images/onboarding/Bustly.svg";
import ProductBrandLogo from "@/components/Brand/ProductBrandLogo";
import bustlyAvatar from "@/assets/images/onboarding/Bustly_.png";
import idCard from "@/assets/images/onboarding/ID_card.svg";
import bytedanceModelIcon from "@/assets/images/pricing-models/bytedance.png";
import claudeModelIcon from "@/assets/images/pricing-models/claude.svg";
import nanoBananaModelIcon from "@/assets/images/pricing-models/nano-banana.svg";
import PricingCard, { type PlanData } from "../Pricing/PricingCard";
import {
  buildPluginPricingPlanData,
  getPluginPlanMemberLimit,
  getPluginPlanPrice,
  PLUGIN_PLAN_FALLBACK_NAME,
  resolvePluginBillingPlans,
  resolvePluginMonthlyPlan,
  resolvePluginPlanForFamily,
} from "../Pricing/pluginPricingCatalog";
import { resolveOnboardingPlanCardState } from "./onboardingPlanSelection";
import { IntegrationDisclosureModal } from "@/components/IntegrationDisclosure";
import ConnectionTrustTags from "./ConnectionTrustTags";
import {
  buildPricingTierCatalogFromPlanComparison,
  getPricingTierBrandGroupLimit,
  getPricingTierForPlanCode,
  getPricingTierIntegrationConnections,
  getPricingTierPrice,
  resolveConfiguredPlanEstimatedOperations,
  resolveConfiguredPlanWorkUnits,
} from "../Pricing/pricingTierOptions";
import { PLUGIN_PLAN_LIMITS } from "@/utils/pluginWorkspace";
import {
  buildInitialOnboardingWorkspacePayload,
  getVisibleOnboardingStepIndex,
  getVisibleOnboardingSteps,
  buildOnboardingWorkspaceUpdatePayload,
  hasReusableOnboardingWorkspace,
  isOnboardingTrialUnavailableConnection,
  removeConnectedIntegrationTrialRequirement,
  resolveAggregatedOnboardingTrialEligibilityState,
  resolveConnectedIntegrationTrialRequirements,
  resolveConnectedStoreProfile,
  resolveIntentSubmissionWorkspaceState,
  resolveOnboardingTrialActivationAction,
  updateConnectedIntegrationTrialRequirements,
} from "@/components/Onboarding/onboardingFlow";
import {
  formatWorkspacePreparationError,
  resolveWorkspacePreparationError,
  type WorkspacePreparationError,
} from "./workspacePreparationError";
import {
  buildCheckoutReturnUrls,
  buildOnboardingAdminSuccessRoute,
} from "../Pricing/paymentUrlUtils";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useToast } from "@/contexts/ToastContext";
import { useDialog } from "@/contexts/DialogContext";
import {
  useWorkspaceIntegrations,
  type DisconnectIntegrationOptions,
  type UseWorkspaceIntegrationsReturn,
} from "@/contexts/AuthContext/useWorkspaceIntegrations";
import { buildShopifyOnboardingConnectRedirectUrl } from "@/contexts/AuthContext/shopifyOnboardingResume";
import { billingService, type Plan, type PlanComparisonCatalogResponse } from "@common/api/billingService";
import {
  resolveOnboardingPlanAvailability,
  resolveOnboardingPlanStoreLimit,
} from "./onboardingPlanEligibility";
import {
  getCurrentProductCode,
  isPluginProduct,
  withProductHeaders,
  withPluginProductHeaders,
} from "@common/api/productContext";
import {
  createOperationId,
  getTrackingErrorCode,
} from "@/lib/behaviorTracking";
import { captureSentryDiagnosticEvent, logSentryEvent } from "@/lib/sentry";
import {
  type Translate as TranslateFn,
  useTranslation,
} from "@client-app/i18n";
import {
  featureService,
  InviteStatusResponse,
  WorkspaceInvitation,
} from "@common/api/featureService";
import {
  authService,
  type GoogleAdsAccountsResponse,
  type StoreTrialEligibilityState,
} from "@common/api/authService";
import type { IntegrationResource } from "@common/api/brandGroupService";
import { Tooltip } from "@common/index";
import {
  ArrowRight,
  CaretDown,
  CaretUp,
  CircleNotch,
  CloudCheck,
  HardDrive,
  LinkBreak,
  PencilSimple,
  Plus,
  SealCheck,
  SignOut,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import InviteSelection from "./InviteSelection";
import Modal from "../Settings/components/Modal";
import IntegrationApiKeyModal from "../Settings/components/integrations/IntegrationApiKeyModal";
import IntegrationCredentialsModal from "../Settings/components/integrations/IntegrationCredentialsModal";
import { getIntegrationConnectionLabelPlaceholder } from "../Settings/components/integrations/connectionLabelCopy";
import GoogleAdsAccountSelectionModal from "../Settings/components/integrations/GoogleAdsAccountSelectionModal";
import type { GoogleAdsAccountSelectionSavedPayload } from "../Settings/components/integrations/GoogleAdsAccountSelectionModal";
import {
  buildGoogleAdsRows,
  normalizeCustomerId,
} from "@/pages/GoogleAdsAccountSelection/googleAdsAccountSelectionModel";
import type { BrandSelectOption } from "../Settings/components/BrandSelectMenu";
import OnboardingResourceSelectionModal from "./OnboardingResourceSelectionModal";
import {
  resolveDefaultBusinessGroupId,
  resolveOnboardingConnectionId,
  resolveOnboardingWorkspaceIntegrationId,
  normalizeOnboardingResourceKey,
  selectOnboardingSecondaryResources,
  loadOnboardingResourceSelectionData,
} from "./onboardingResourceSelection";
import {
  buildIntegrationCredentialConnection,
  canSubmitIntegrationCredentialForm,
  createIntegrationCredentialValues,
  isIntegrationCredentialTool,
  type IntegrationCredentialTool,
} from "../Settings/components/integrations/integrationCredentialForm";
import { resolveIntegrationConnectEntryPoint } from "../Settings/components/integrations/bustlyHostedConnectProviders";
import {
  getBrandIcon,
  getBrandIconFrameClassName,
  getBrandIconImageClassName,
} from "../Settings/components/integrations/IntegrationCard";
import {
  getIntegrationProviderForTool,
  normalizeIntegrationProvider,
  requiresManualCredentialInput,
  type ConnectableIntegrationScenario,
} from "../Settings/components/integrations/integrationCatalog";
import {
  normalizeConnectRestriction,
  type ConnectRestrictionPayload,
} from "../Settings/components/integrations/connectRestriction";
import StoreConnectionModal, {
  STORE_URL_INPUT_CONFIG,
} from "./StoreConnectionModal";
import { normalizeShopifyStoreDomainInput } from "./storeUrlNormalization";
import { useWorkspacePolling } from "./useWorkspacePolling";
import {
  inviteEmailMatchesCurrentUser,
  resolveCurrentInviteEmails,
  resolveCurrentInviteEmail,
  shouldAutoAcceptInviteOnOnboarding,
  shouldEnterAcceptedInviteWorkspaceOnOnboarding,
  shouldProcessInviteTokenOnOnboarding,
} from "./inviteAcceptanceFlow";
import {
  buildInviteSuccessAdminRoute,
  resolveInviteAcceptWorkspaceId,
} from "./inviteReturn";
import {
  canContinueOnboardingConnectionStep,
  BUSTLY_CORE_ONBOARDING_MIN_CONNECTION_COUNT,
  getOnboardingConnectionCategories,
  getOnboardingConnectionToolsForCategory,
  getVisibleOnboardingConnectionCategories,
  ONBOARDING_CONNECTION_CATEGORIES,
  PLUGIN_ONBOARDING_EXCLUDED_CATEGORY_IDS,
  PLUGIN_ONBOARDING_MIN_CONNECTION_COUNT,
  resolveOnboardingConnectionStepLoading,
  resolveOnboardingConnectionRequirements,
  resolveOnboardingPlanStepAccess,
  resolveOnboardingStepForRendering,
  type OnboardingConnectionBrowseCategoryId,
  type OnboardingConnectionCategory,
  type OnboardingConnectionCategoryId,
  type OnboardingConnectionRequirementBasis,
} from "./connectionRequirements";
import { resolveOnboardingConnectionFeedback } from "./connectionFeedback";
import {
  buildOnboardingConnectionDisconnectOptions,
  BUSTLY_CORE_ONBOARDING_LIMITS,
  getOnboardingConnectionKind,
  hasDuplicateOnboardingStoreConnection,
  resolveOnboardingStoreConnectionIdentity,
  resolveOnboardingConnectionInstances,
  resolveOnboardingConnectionAdditionState,
  resolveOnboardingConnectionUsage,
  resolvePluginConnectionUsage,
  type OnboardingConnectionInstance,
  type PluginConnectionAdditionState,
} from "./onboardingConnectionInstances";
import { refreshOnboardingConnectionSnapshot } from "./onboardingConnectionSync";
import {
  readStoredReferralAttribution,
} from "@common/referral/referralAttribution";
import {
  shouldReleaseOnboardingPlanLoading,
  type OnboardingCheckoutNavigationState,
} from "./planCheckoutNavigation";

const BUSTLY_AVATAR_SRC = bustlyAvatar;
const PLUGIN_CAPACITY_FULL_CODE = "4020";
const PLUGIN_ACCESS_LIMITED_ROUTE = "/plugin/access-limited";

const isPluginCapacityFullError = (error: any): boolean => {
  const code = String(error?.code || error?.response?.code || "");
  return code === PLUGIN_CAPACITY_FULL_CODE;
};

export { normalizeShopifyStoreDomainInput } from "./storeUrlNormalization";

interface OnboardingIntegrationOption {
  id: string;
  provider: string;
  categoryId: OnboardingConnectionCategoryId;
  scenario: ConnectableIntegrationScenario;
}

export const buildOnboardingConnectionLabelConfig = ({
  label,
}: {
  label: string;
}): { connection_alias?: string } => {
  const connectionAlias = label.trim();
  return connectionAlias ? { connection_alias: connectionAlias } : {};
};

// Kept as a compatibility export for callers that still use the old name.
export const buildPluginConnectionLabelConfig = ({
  label,
}: {
  isPluginOnboarding: boolean;
  label: string;
}): { connection_alias?: string } =>
  buildOnboardingConnectionLabelConfig({ label });

export const resolveOnboardingConnectionExpansionTarget = ({
  platformId,
  isConnected,
  platformOptions,
}: {
  platformId: string;
  isConnected: boolean;
  platformOptions: readonly Pick<
    OnboardingIntegrationOption,
    "id" | "categoryId"
  >[];
}) => {
  if (!isConnected) {
    return null;
  }

  const platform = platformOptions.find((option) => option.id === platformId);
  return platform
    ? { tool: platform.id, categoryId: platform.categoryId }
    : null;
};

export const resolvePluginConnectionExpansionTarget = ({
  isPluginOnboarding,
  platformId,
  isConnected,
  platformOptions,
}: {
  isPluginOnboarding: boolean;
  platformId: string;
  isConnected: boolean;
  platformOptions: readonly Pick<
    OnboardingIntegrationOption,
    "id" | "categoryId"
  >[];
}) => {
  if (!isPluginOnboarding || !isConnected) {
    return null;
  }

  return resolveOnboardingConnectionExpansionTarget({
    platformId,
    isConnected,
    platformOptions,
  });
};

const buildOnboardingIntegrationOptions = (
  categories: readonly OnboardingConnectionCategory[],
): OnboardingIntegrationOption[] =>
  categories.flatMap((category) =>
    getOnboardingConnectionToolsForCategory(category).flatMap(
      (tool) => {
        const provider = getIntegrationProviderForTool(tool);
        return provider
          ? [
              {
                id: tool,
                provider,
                categoryId: category.id,
                scenario: category.scenario,
              },
            ]
          : [];
      },
    ),
  );

const ONBOARDING_INTEGRATION_OPTIONS = buildOnboardingIntegrationOptions(
  ONBOARDING_CONNECTION_CATEGORIES,
);

const ONBOARDING_CATEGORY_LABEL_KEYS: Record<
  OnboardingConnectionBrowseCategoryId,
  string
> = {
  store_ops: "website.onboarding.connect.categories.storeOps",
  advertising: "website.onboarding.connect.categories.advertising",
  email_marketing: "website.onboarding.connect.categories.emailMarketing",
  marketing: "website.onboarding.connect.categories.marketing",
  analytics: "website.onboarding.connect.categories.analytics",
  seo: "website.onboarding.connect.categories.seo",
  social_media: "website.onboarding.connect.categories.socialMedia",
  customers: "website.onboarding.connect.categories.customers",
};

const ONBOARDING_CATEGORY_DESCRIPTION_KEYS: Record<
  OnboardingConnectionBrowseCategoryId,
  string
> = {
  store_ops: "website.onboarding.connect.categories.descriptions.storeOps",
  advertising: "website.onboarding.connect.categories.descriptions.advertising",
  email_marketing:
    "website.onboarding.connect.categories.descriptions.emailMarketing",
  marketing: "website.onboarding.connect.categories.descriptions.marketing",
  analytics: "website.onboarding.connect.categories.descriptions.analytics",
  seo: "website.onboarding.connect.categories.descriptions.seo",
  social_media:
    "website.onboarding.connect.categories.descriptions.socialMedia",
  customers: "website.onboarding.connect.categories.descriptions.customers",
};

// Store Ops browsing includes shipping; only storefront identities use store
// metadata, URL handling and duplicate-store checks.
const isStoreOpsProvider = (provider?: string | null) =>
  getOnboardingConnectionKind(provider) === "store";

const getConnectedOnboardingOptions = (
  integrations: readonly {
    provider?: string | null;
    connected?: boolean | null;
  }[],
  platformOptions: readonly OnboardingIntegrationOption[],
) =>
  platformOptions.filter((option) =>
    integrations.some(
      (integration) =>
        integration.connected === true &&
        normalizeIntegrationProvider(integration.provider) ===
          normalizeIntegrationProvider(option.provider),
    ),
  );

const TRIAL_FALLBACK_WORK_UNITS = 400;
const WORK_UNIT_DISPLAY_MICROUSD = 100_000;

const toPositiveNumberOrNull = (value: unknown): number | null => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const resolveOnboardingTrialWorkUnits = (
  plans: readonly Plan[],
): number => {
  const freePlan = plans.find(
    (plan) => String(plan.code || "").toUpperCase() === "FREE",
  );
  const rawFreePlan = freePlan as (Plan & Record<string, unknown>) | undefined;
  const displayBudgetMicrousd = toPositiveNumberOrNull(
    rawFreePlan?.displayBudgetMicrousdMonthly ??
      rawFreePlan?.display_budget_microusd_monthly ??
      rawFreePlan?.workUnitsBudgetDisplayMicrousdMonthly ??
      rawFreePlan?.work_units_budget_display_microusd_monthly,
  );

  if (displayBudgetMicrousd !== null) {
    return Math.floor(displayBudgetMicrousd / WORK_UNIT_DISPLAY_MICROUSD);
  }

  return TRIAL_FALLBACK_WORK_UNITS;
};

const V13AssistantHint: React.FC<{ lines: string[] }> = ({ lines }) => (
  <div className="mb-8 flex w-full items-center justify-center gap-3">
    <div className="flex h-[44px] w-[44px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#272729] shadow-sm ring-2 ring-white/80">
      <img
        src={BUSTLY_AVATAR_SRC}
        alt="Bustly"
        className="h-full w-full object-cover"
      />
    </div>
    <div className="rounded-[20px] border border-[#E0E3EC] bg-white px-5 py-3 text-left shadow-[0_12px_28px_rgba(26,22,47,0.06)]">
      <div className="flex items-center gap-3">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#6E5BFF] opacity-70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#1A162F]" />
        </span>
        <div className="text-[14px] font-medium leading-[1.35] text-[#1A162F] sm:whitespace-nowrap">
          {lines.join(" ")}
        </div>
      </div>
    </div>
  </div>
);

const getOnboardingMonthlyPlan = (plans: Plan[], planCode: string) =>
  plans.find(
    (plan) => plan.code === planCode && plan.billingCycle === "monthly",
  );

export const buildOnboardingPlanOptions = (
  t: TranslateFn,
  plans: Plan[] = [],
  locale = "en",
  trialWorkUnitsLabel = "",
  isPluginOnboarding = false,
  pluginButtonText?: string,
  pluginPlansUnavailable = false,
  trialEligibilityState?: string | null,
  comparisonCatalog?: PlanComparisonCatalogResponse | null,
) => {
  const numberFormatter = new Intl.NumberFormat(locale);
  const isTrialAvailable = trialEligibilityState !== "checkout_required";
  const isChinese = locale === "zh-CN";
  const pluginBackendPlans = resolvePluginBillingPlans(plans);

  if (isPluginOnboarding) {
    if (pluginPlansUnavailable) {
      return [];
    }

    if (pluginBackendPlans.length === 0) {
      return ([
        {
          id: "PLUGIN_SOLO_19",
          planCode: "PLUGIN_SOLO_19",
          title: PLUGIN_PLAN_FALLBACK_NAME,
          pricingCard: buildPluginPricingPlanData({
            t,
            planName: PLUGIN_PLAN_FALLBACK_NAME,
            planFamily: "solo",
            memberLimit: 1,
            price: 19,
            buttonText:
              pluginButtonText || t("website.onboarding.plan.pluginTrialCta"),
          }),
        },
        {
          id: "PLUGIN_TEAM_99",
          planCode: "PLUGIN_TEAM_99",
          title: "Team",
          pricingCard: buildPluginPricingPlanData({
            t,
            planName: "Team",
            planFamily: "team",
            memberLimit: 10,
            price: 99,
            buttonText:
              pluginButtonText || t("website.onboarding.plan.pluginTrialCta"),
          }),
        },
      ] as const);
    }

    return (["solo", "team"] as const)
      .map((family) => {
        const selectedPlan = resolvePluginPlanForFamily(
          pluginBackendPlans,
          family,
        );
        if (!selectedPlan) return null;

        return {
          id: selectedPlan.code,
          planCode: selectedPlan.code,
          title: selectedPlan.name || (family === "team" ? "Team" : PLUGIN_PLAN_FALLBACK_NAME),
          pricingCard: buildPluginPricingPlanData({
            t,
            planName: selectedPlan.name || (family === "team" ? "Team" : PLUGIN_PLAN_FALLBACK_NAME),
            price: getPluginPlanPrice(selectedPlan),
            planFamily: family,
            memberLimit: getPluginPlanMemberLimit(selectedPlan),
            buttonText:
              pluginButtonText || t("website.onboarding.plan.pluginTrialCta"),
          }),
        };
      })
      .filter(
        (
          option,
        ): option is NonNullable<typeof option> => option !== null,
      );
  }

  const soloBackendPlan = getOnboardingMonthlyPlan(plans, "SOLO_199");
  const teamBackendPlan = getOnboardingMonthlyPlan(plans, "TEAM_1499");
  const soloCatalogTier = getPricingTierForPlanCode("solo", "SOLO_199");
  const teamCatalogTier = getPricingTierForPlanCode("team", "TEAM_1499");
  const connectionCatalog = buildPricingTierCatalogFromPlanComparison(comparisonCatalog);
  const soloConnectionTier = connectionCatalog.soloTiers.find((tier) => tier.planCode === "SOLO_199");
  const teamConnectionTier = connectionCatalog.teamTiers.find((tier) => tier.planCode === "TEAM_1499");
  const getBrandGroupBenefits = (
    limit: number | "unlimited" | undefined,
  ) => {
    if (!limit) return [];
    return [{
      id: "brand-groups",
      label:
        limit === "unlimited"
          ? t("website.pricing.unlimitedBrandGroupsIncluded")
          : t(limit === 1 ? "website.pricing.brandGroupIncluded" : "website.pricing.brandGroupsIncluded", {
              value: numberFormatter.format(limit),
            }),
    }];
  };
  const soloBrandGroupLimit = soloCatalogTier
    ? getPricingTierBrandGroupLimit(soloCatalogTier)
    : 0;
  const teamBrandGroupLimit = teamCatalogTier
    ? getPricingTierBrandGroupLimit(teamCatalogTier)
    : 0;
  const soloWorkUnits =
    resolveConfiguredPlanWorkUnits(soloBackendPlan) ??
    soloCatalogTier?.workUnits ??
    0;
  const teamWorkUnits =
    resolveConfiguredPlanWorkUnits(teamBackendPlan) ??
    teamCatalogTier?.workUnits ??
    0;
  const soloEstimatedOperations =
    resolveConfiguredPlanEstimatedOperations(soloBackendPlan) ??
    soloCatalogTier?.estimatedMonthlyOperations;
  const teamEstimatedOperations =
    resolveConfiguredPlanEstimatedOperations(teamBackendPlan) ??
    teamCatalogTier?.estimatedMonthlyOperations;

  const sharedTrialDetails = {
    pricePrefix: t("website.onboarding.plan.startingPriceLabel"),
    priceSuffix: t("website.onboarding.plan.startingPriceSuffix"),
    pricePresentation:
      locale === "zh-CN" ? ("leading-period" as const) : ("default" as const),
    trialBenefit: isTrialAvailable ? {
      value: trialWorkUnitsLabel,
    } : undefined,
    workspaceGroupLabel: t("website.onboarding.plan.featureGroups.workspace"),
    builtInCapabilitiesLabel: t(
      "website.onboarding.plan.featureGroups.builtInAiCapabilities",
    ),
    showWorkUnitBenefitsInOnboarding: true,
    showTrainingAndIntegrationsInOnboarding: true,
    showIntegrations: true,
    alignFeatureSections: true,
    builtInModels: [
      {
        name: "Nano Banana 2",
        iconSrc: nanoBananaModelIcon,
        capabilityLabel: t(
          "website.onboarding.plan.featureGroups.createImages",
        ),
      },
      {
        name: "Seedance 2.0",
        iconSrc: bytedanceModelIcon,
        capabilityLabel: t(
          "website.onboarding.plan.featureGroups.createVideos",
        ),
      },
      {
        name: "Opus 4.8",
        iconSrc: claudeModelIcon,
        capabilityLabel: t(
          "website.onboarding.plan.featureGroups.handleBusinessTasks",
        ),
      },
    ],
  };

  return [
    {
      id: "Solo",
      planCode: "SOLO_199",
      title: t("website.onboarding.plan.solo.title"),
      pricingCard: {
        ...sharedTrialDetails,
        name: t("website.onboarding.plan.solo.title"),
        price: soloCatalogTier
          ? getPricingTierPrice(soloCatalogTier, "monthly")
          : 199,
        description: t("website.onboarding.plan.solo.description"),
        buttonText: t(isTrialAvailable ? "website.onboarding.plan.solo.cta" : "website.pricing.startSubscription"),
        featureMode: "grouped",
        featureGroups: {
          workspace: [
            {
              id: "workspace-users",
              label: t("website.onboarding.plan.solo.lowestTierBullet1"),
            },
            ...(isTrialAvailable ? [{
              id: "trial-connections",
              label: t("website.onboarding.plan.trialConnections", {
                total: BUSTLY_CORE_ONBOARDING_LIMITS.totalIntegrationConnections,
                stores: BUSTLY_CORE_ONBOARDING_LIMITS.storeConnections,
                storePlural: BUSTLY_CORE_ONBOARDING_LIMITS.storeConnections === 1 ? "" : "s",
              }),
            }] : []),
            {
              id: "connections",
              label: t(isTrialAvailable ? "website.onboarding.plan.solo.lowestTierBullet2" : "website.pricing.connectionsIncluded", {
                total: soloConnectionTier ? getPricingTierIntegrationConnections(soloConnectionTier).total : 0,
                stores: resolveOnboardingPlanStoreLimit(soloBackendPlan ?? { code: "SOLO_199" }),
                storePlural: resolveOnboardingPlanStoreLimit(soloBackendPlan ?? { code: "SOLO_199" }) === 1 ? "" : "s",
              }),
            },
            ...getBrandGroupBenefits(soloBrandGroupLimit),
          ],
          workUnits: [
            {
              id: "monthly-work-units",
              label: t(isTrialAvailable ? "website.onboarding.plan.solo.lowestTierBullet3" : "website.pricing.workUnitsFeature", {
                value: numberFormatter.format(soloWorkUnits),
              }),
            },
            {
              id: "work-unit-top-ups",
              label: t("website.onboarding.plan.featureGroups.workUnitTopUps"),
            },
          ],
          trainingAndIntegrations: [
            {
              id: "recorded-courses",
              label: t("website.pricing.recordedCoursesIncluded"),
            },
          ],
        },
        estimatedCapacity: soloEstimatedOperations
          ? t("website.onboarding.plan.solo.lowestTierCapacity", {
              min: numberFormatter.format(soloEstimatedOperations.min),
              max: numberFormatter.format(soloEstimatedOperations.max),
            })
          : undefined,
      } satisfies PlanData,
    },
    {
      id: "Team",
      planCode: "TEAM_1499",
      title: t("website.onboarding.plan.team.title"),
      pricingCard: {
        ...sharedTrialDetails,
        name: t("website.onboarding.plan.team.title"),
        price: teamCatalogTier
          ? getPricingTierPrice(teamCatalogTier, "monthly")
          : 1499,
        description: t("website.onboarding.plan.team.description"),
        buttonText: t(isTrialAvailable ? "website.onboarding.plan.team.cta" : "website.pricing.startSubscription"),
        featureMode: "grouped",
        featureGroups: {
          workspace: [
            {
              id: "workspace-users",
              label: t("website.onboarding.plan.team.lowestTierBullet1"),
            },
            ...(isTrialAvailable ? [{
              id: "trial-connections",
              label: t("website.onboarding.plan.trialConnections", {
                total: BUSTLY_CORE_ONBOARDING_LIMITS.totalIntegrationConnections,
                stores: BUSTLY_CORE_ONBOARDING_LIMITS.storeConnections,
                storePlural: BUSTLY_CORE_ONBOARDING_LIMITS.storeConnections === 1 ? "" : "s",
              }),
            }] : []),
            {
              id: "connections",
              label: t(isTrialAvailable ? "website.onboarding.plan.team.lowestTierBullet2" : "website.pricing.connectionsIncluded", {
                total: teamConnectionTier ? getPricingTierIntegrationConnections(teamConnectionTier).total : 0,
                stores: resolveOnboardingPlanStoreLimit(teamBackendPlan ?? { code: "TEAM_1499" }),
                storePlural: resolveOnboardingPlanStoreLimit(teamBackendPlan ?? { code: "TEAM_1499" }) === 1 ? "" : "s",
              }),
            },
            ...getBrandGroupBenefits(teamBrandGroupLimit),
          ],
          workUnits: [
            {
              id: "monthly-work-units",
              label: t(isTrialAvailable ? "website.onboarding.plan.team.lowestTierBullet3" : "website.pricing.sharedWorkUnitsFeature", {
                value: numberFormatter.format(teamWorkUnits),
              }),
            },
            {
              id: "work-unit-top-ups",
              label: t("website.onboarding.plan.featureGroups.workUnitTopUps"),
            },
          ],
          trainingAndIntegrations: [
            {
              id: "recorded-courses",
              label: t("website.pricing.recordedCoursesIncluded"),
            },
            {
              id: "weekly-live-sessions",
              label: t("website.pricing.weeklyLiveSessionsIncluded"),
              badge: "team-exclusive",
            },
            {
              id: "onsite-training",
              label: t("website.pricing.onsiteTrainingIncluded"),
              badge: "team-exclusive",
            },
            {
              id: "team-collaboration-integrations",
              label: t(
                "website.pricing.teamCollaborationIntegrationsIncluded",
              ),
            },
          ],
        },
        estimatedCapacity: teamEstimatedOperations
          ? t("website.onboarding.plan.team.lowestTierCapacity", {
              min: numberFormatter.format(teamEstimatedOperations.min),
              max: numberFormatter.format(teamEstimatedOperations.max),
            })
          : undefined,
        highlightLimits: true,
      } satisfies PlanData,
    },
  ];
};

type OnboardingPlanOption = ReturnType<typeof buildOnboardingPlanOptions>[number];

const buildIntentOptions = (t: TranslateFn) => [
  {
    id: "ads",
    label: t("website.onboarding.intent.options.ads.label"),
    desc: t("website.onboarding.intent.options.ads.description"),
  },
  {
    id: "conversion",
    label: t("website.onboarding.intent.options.conversion.label"),
    desc: t("website.onboarding.intent.options.conversion.description"),
  },
  {
    id: "retention",
    label: t("website.onboarding.intent.options.retention.label"),
    desc: t("website.onboarding.intent.options.retention.description"),
  },
  {
    id: "execution",
    label: t("website.onboarding.intent.options.execution.label"),
    desc: t("website.onboarding.intent.options.execution.description"),
  },
  {
    id: "other",
    label: t("website.onboarding.intent.options.other.label"),
    desc: "",
  },
];

const buildExpiryText = (
  daysUntilExpiry: number | undefined,
  t: TranslateFn,
) => {
  if (typeof daysUntilExpiry !== "number") {
    return null;
  }

  if (daysUntilExpiry < 0) {
    return t("website.onboarding.invite.expired");
  }

  if (daysUntilExpiry === 0) {
    return t("website.onboarding.invite.expiresToday");
  }

  if (daysUntilExpiry === 1) {
    return t("website.onboarding.invite.expiresInOneDay");
  }

  return t("website.onboarding.invite.expiresInDays", {
    days: daysUntilExpiry,
  });
};

const normalizeEmail = (email?: string | null) =>
  (email || "").trim().toLowerCase();

const createUpdatedConnectionList = (current: string[], label: string) =>
  Array.from(new Set([...(current || []), label]));

const matchesIntegrationProvider = (
  integrationProvider: string | undefined,
  expectedProvider: string,
) =>
  normalizeIntegrationProvider(integrationProvider) ===
  normalizeIntegrationProvider(expectedProvider);

const FREE_TRIAL_UNAVAILABLE_RESTRICTION_STATES =
  new Set<StoreTrialEligibilityState>([
    "already_bound",
    "trial_valid",
    "trial_expired",
  ]);

const mergeEligibilityPayload = (
  latest?: ConnectRestrictionPayload | null,
  fallback?: ConnectRestrictionPayload | null,
): ConnectRestrictionPayload | undefined => {
  if (!latest) {
    return fallback ?? undefined;
  }

  if (!fallback) {
    return latest;
  }

  return {
    restrictionState:
      latest.restrictionState ??
      latest.eligibilityState ??
      fallback.restrictionState ??
      fallback.eligibilityState,
    eligibilityState: latest.eligibilityState ?? fallback.eligibilityState,
    blockConnect: latest.blockConnect ?? fallback.blockConnect,
    upgradeRequired: latest.upgradeRequired ?? fallback.upgradeRequired,
    message:
      latest.message ??
      latest.eligibilityMessage ??
      fallback.message ??
      fallback.eligibilityMessage,
    eligibilityMessage:
      latest.eligibilityMessage ?? fallback.eligibilityMessage,
    allowsFreshTrial: latest.allowsFreshTrial ?? fallback.allowsFreshTrial,
    trialRemainingDays:
      latest.trialRemainingDays ?? fallback.trialRemainingDays,
    currentWorkspaceId:
      latest.currentWorkspaceId ?? fallback.currentWorkspaceId,
    currentWorkspaceName:
      latest.currentWorkspaceName ?? fallback.currentWorkspaceName,
    currentStoreCount: latest.currentStoreCount ?? fallback.currentStoreCount,
    storeLimit: latest.storeLimit ?? fallback.storeLimit,
    upgradeTarget: latest.upgradeTarget ?? fallback.upgradeTarget,
  };
};

const buildFallbackStoreName = (platformId: string, rawStoreUrl: string) => {
  const normalizedStoreUrl = rawStoreUrl.trim();

  if (!normalizedStoreUrl) {
    return "";
  }

  if (platformId === "Shopify") {
    return `${normalizedStoreUrl}.myshopify.com`;
  }

  if (platformId === "BigCommerce") {
    return `store-${normalizedStoreUrl}.mybigcommerce.com`;
  }

  return normalizedStoreUrl;
};

export const isFreeTrialUnavailableRestriction = (restriction: {
  restrictionState: StoreTrialEligibilityState | null;
  allowsFreshTrial?: boolean | null;
  message: string | null;
  trialRemainingDays: number | null;
}) =>
  restriction.restrictionState !== "trial_resume_allowed" &&
  Boolean(
    restriction.allowsFreshTrial === false ||
      (restriction.restrictionState &&
        FREE_TRIAL_UNAVAILABLE_RESTRICTION_STATES.has(
          restriction.restrictionState,
        )),
  );

const inferConnectedTrialRestrictionState = (
  restriction: {
    allowsFreshTrial: boolean | null;
    currentWorkspaceId: string | null;
    message: string | null;
    restrictionState: StoreTrialEligibilityState | null;
    trialRemainingDays: number | null;
  },
  currentWorkspaceId?: string | null,
): StoreTrialEligibilityState | null => {
  if (restriction.restrictionState || restriction.allowsFreshTrial !== false) {
    return restriction.restrictionState;
  }

  const normalizedCurrentWorkspaceId = currentWorkspaceId?.trim() || null;
  const normalizedRestrictionWorkspaceId =
    restriction.currentWorkspaceId?.trim() || null;

  if (
    normalizedRestrictionWorkspaceId &&
    normalizedCurrentWorkspaceId &&
    normalizedRestrictionWorkspaceId !== normalizedCurrentWorkspaceId
  ) {
    return "already_bound";
  }

  if (
    restriction.message &&
    /already (?:connected|bound) to another workspace/i.test(
      restriction.message,
    )
  ) {
    return "already_bound";
  }

  if (
    typeof restriction.trialRemainingDays === "number" &&
    restriction.trialRemainingDays > 0
  ) {
    return "trial_valid";
  }

  return "trial_expired";
};

interface PluginStepConnectIntegrationCardProps {
  tool: string;
  instances: readonly OnboardingConnectionInstance[];
  isRestoringConnection: boolean;
  isExpanded: boolean;
  isSyncing: boolean;
  hasTrialUsed?: boolean;
  additionState: PluginConnectionAdditionState;
  limitMessage?: string;
  onConnect: () => void;
  onToggle: () => void;
  details?: React.ReactNode;
  connectionProgress?: React.ReactNode;
}

interface StepConnectIntegrationLayoutProps {
  children: React.ReactNode;
}

export const StepConnectIntegrationLayout: React.FC<StepConnectIntegrationLayoutProps> =
  ({ children }) => (
    <div
      data-onboarding-integration-layout="provider-accordion"
      className="flex w-full flex-col gap-3"
    >
      {children}
    </div>
  );

export const PluginStepConnectIntegrationCard: React.FC<PluginStepConnectIntegrationCardProps> =
  React.memo(
    ({
      tool,
      instances,
      isRestoringConnection,
      isExpanded,
      isSyncing,
      hasTrialUsed,
      additionState,
      limitMessage,
      onConnect,
      onToggle,
      details,
      connectionProgress,
    }) => {
      const { t } = useTranslation();
      const validCount = instances.filter(
        (instance) => instance.isValidConnection,
      ).length;
      const hasInstances = instances.length > 0;
      const showsConnectionState = hasInstances || isRestoringConnection;
      const hasAttentionState = instances.some(
        (instance) => !instance.isValidConnection,
      );
      const limitDescriptionId = `onboarding-connection-limit-${tool
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}`;
      const isAdditionBlocked = !hasInstances && !additionState.allowed;

      const handleCardAction = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (isSyncing || isRestoringConnection) return;
        if (!additionState.allowed) return;
        onConnect();
      };

      return (
        <div
          data-onboarding-integration-card={tool}
          className={clsx(
            "group flex min-h-[76px] w-full flex-col overflow-hidden rounded-2xl border transition-all duration-200",
            showsConnectionState
              ? "border-[#D9DCE6] bg-white shadow-[0_4px_14px_rgba(26,22,47,0.04)]"
              : "border-[#E6E8F0] bg-white shadow-[0_4px_14px_rgba(26,22,47,0.04)] hover:border-[#1A162F] hover:shadow-[0_8px_22px_rgba(26,22,47,0.07)]",
          )}
        >
          <div
            role="button"
            tabIndex={0}
            aria-expanded={hasInstances ? isExpanded : undefined}
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("button")) {
                return;
              }
              if (hasInstances) {
                onToggle();
              } else {
                if (isSyncing || isRestoringConnection) return;
                if (!additionState.allowed) return;
                onConnect();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                if ((event.target as HTMLElement).closest("button")) {
                  return;
                }
                event.preventDefault();
                if (hasInstances) {
                  onToggle();
                } else {
                  if (isSyncing || isRestoringConnection) return;
                  if (!additionState.allowed) return;
                  onConnect();
                }
              }
            }}
            className="flex min-h-[76px] w-full cursor-pointer select-none items-center justify-between gap-3 px-4 py-3.5 outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 focus-visible:ring-offset-1"
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div
                className={clsx(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                  getBrandIconFrameClassName(tool),
                )}
              >
                <img
                  src={getBrandIcon(tool)}
                  alt=""
                  className={getBrandIconImageClassName(tool, "h-6 w-6")}
                />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="truncate text-[15px] font-bold leading-tight text-[#1A162F]">
                    {tool}
                  </div>
                  {hasTrialUsed && (!hasInstances || !isExpanded) ? (
                    <span
                      className="inline-flex shrink-0 items-center rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
                      title={t("website.onboarding.notice.freeTrialUsedAppTooltip")}
                    >
                      {t("website.onboarding.notice.freeTrialUsedTag")}
                    </span>
                  ) : null}
                </div>
                {showsConnectionState ? (
                  <div
                    className={clsx(
                      "mt-1 flex items-center gap-1.5 text-[12px] font-medium",
                      isRestoringConnection
                        ? "text-[#8A93B2]"
                        : hasAttentionState
                          ? "text-amber-700"
                          : "text-[#388E3C]",
                    )}
                  >
                    {isRestoringConnection ? (
                      <>
                        <CircleNotch
                          size={12}
                          weight="bold"
                          className="animate-spin text-[#8A93B2]"
                        />
                        <span className="h-3 w-14 animate-pulse rounded bg-[#E6E8F0]" />
                      </>
                    ) : (
                      <>
                        <span
                          className={clsx(
                            "h-1.5 w-1.5 rounded-full",
                            hasAttentionState
                              ? "bg-amber-500"
                              : "bg-[#52C41A]",
                          )}
                        />
                        {hasAttentionState
                          ? t("website.integrationsSettings.attentionNeeded")
                          : t("website.integrationsSettings.connectionCount", {
                              count: validCount,
                              plural: validCount === 1 ? "" : "s",
                            })}
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            {(() => {
              const isCardAdditionDisabled =
                (!hasInstances && isAdditionBlocked) ||
                (hasInstances && !additionState.allowed);

              const actionBtn = (
                <button
                  type="button"
                  onClick={handleCardAction}
                  disabled={
                    isSyncing ||
                    isRestoringConnection ||
                    isCardAdditionDisabled
                  }
                  title={isCardAdditionDisabled ? limitMessage : undefined}
                  aria-describedby={
                    isCardAdditionDisabled && limitMessage
                      ? limitDescriptionId
                      : undefined
                  }
                  aria-controls={
                    hasInstances
                      ? `onboarding-connection-details-${tool
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`
                      : undefined
                  }
                  aria-expanded={hasInstances ? isExpanded : undefined}
                  aria-label={
                    isRestoringConnection
                      ? `${t("website.integrationsSettings.connecting")} ${tool}`
                      : hasInstances
                        ? `${t("website.integrationsSettings.addConnection")} ${tool}`
                        : t("website.onboarding.connect.connectToolAction", {
                            tool,
                          })
                  }
                  className={clsx(
                    "flex shrink-0 items-center justify-center gap-1.5 rounded-full border px-4 py-2 text-[13px] font-bold transition-all",
                    hasInstances
                      ? additionState.allowed
                        ? "border-[#1A162F] bg-[#1A162F] text-white hover:bg-[#241F3D]"
                        : "border-[#E6E8F0] bg-[#F4F5F8] text-[#9AA1B5] cursor-not-allowed"
                      : showsConnectionState
                        ? "border-[#D9DCE6] bg-white text-[#1A162F] hover:border-[#1A162F]"
                        : additionState.allowed
                          ? "border-[#E6E8F0] bg-white text-[#1A162F] group-hover:bg-[#1A162F] group-hover:border-[#1A162F] group-hover:text-white hover:bg-[#1A162F] hover:border-[#1A162F] hover:text-white"
                          : "border-[#E6E8F0] bg-[#F4F5F8] text-[#9AA1B5]",
                  )}
                >
                  {isSyncing || isRestoringConnection ? (
                    <CircleNotch size={16} weight="bold" className="animate-spin" />
                  ) : hasInstances ? (
                    <>
                      <Plus size={14} weight="bold" />
                      <span>{t("website.integrationsSettings.addConnection")}</span>
                    </>
                  ) : (
                    t("website.onboarding.connect.connect")
                  )}
                </button>
              );

              if (isCardAdditionDisabled && limitMessage) {
                return (
                  <Tooltip content={limitMessage} side="top">
                    <span className="inline-block cursor-not-allowed">{actionBtn}</span>
                  </Tooltip>
                );
              }

              return actionBtn;
            })()}
            {isAdditionBlocked && limitMessage ? (
              <span id={limitDescriptionId} className="sr-only">
                {limitMessage}
              </span>
            ) : null}
          </div>

          {connectionProgress}
          {hasInstances && details ? (
            <div
              id={`onboarding-connection-details-${tool
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}`}
              data-onboarding-connection-details={tool}
              className={clsx(
                "grid transition-[grid-template-rows,opacity,padding,border-color] duration-300 ease-in-out",
                isExpanded
                  ? "grid-rows-[1fr] opacity-100 border-t border-[#E6E8F0] px-4 pb-4 pt-4"
                  : "grid-rows-[0fr] opacity-0 border-t border-transparent px-4 py-0 pointer-events-none",
              )}
            >
              <div className="overflow-hidden">
                {isExpanded ? details : null}
              </div>
            </div>
          ) : null}
        </div>
      );
    },
  );

const getConnectionStatusPresentation = (
  instance: OnboardingConnectionInstance,
) => {
  if (instance.status === "connected") {
    return {
      key: "website.integrationsSettings.connected",
      dotClassName: "bg-[#52C41A]",
      textClassName: "text-[#388E3C]",
    };
  }
  if (instance.status === "pending") {
    return {
      key: "website.integrationsSettings.connecting",
      dotClassName: "bg-amber-500",
      textClassName: "text-amber-700",
    };
  }
  if (instance.status === "error") {
    return {
      key: "website.integrationsSettings.attentionNeeded",
      dotClassName: "bg-red-500",
      textClassName: "text-red-600",
    };
  }
  return {
    key: "website.integrationsSettings.disconnected",
    dotClassName: "bg-[#9AA1B5]",
    textClassName: "text-[#666F8D]",
  };
};

const InviteStateCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => (
  <div className="w-full max-w-lg px-6">
    <div className="rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600">
        {icon}
      </div>
      <h1 className="mb-3 text-2xl font-bold text-text-main">{title}</h1>
      <div className="space-y-2 leading-relaxed text-text-sub">
        {description}
      </div>
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  </div>
);

const formatInviteRoleName = (
  role: string | null | undefined,
  t: TranslateFn,
) => {
  const trimmedRole = String(role || "").trim();

  if (!trimmedRole) {
    return t("website.membersSettings.memberRole");
  }

  const normalized = trimmedRole.toUpperCase();
  if (normalized === "OWNER") return t("website.membersSettings.ownerRole");
  if (normalized === "ADMIN") return t("website.membersSettings.adminRole");
  if (normalized === "MEMBER") return t("website.membersSettings.memberRole");

  if (
    trimmedRole === trimmedRole.toUpperCase() ||
    trimmedRole === trimmedRole.toLowerCase()
  ) {
    return trimmedRole
      .replace(/[_-]+/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map(
        (segment) =>
          segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase(),
      )
      .join(" ");
  }

  return trimmedRole;
};

const getInviteRoleArticle = (roleName: string) =>
  /^[aeiou]/i.test(roleName.trim()) ? "an" : "a";

const getWorkspaceInitial = (workspaceName?: string | null) => {
  const trimmedName = String(workspaceName || "").trim();
  return trimmedName ? trimmedName.charAt(0).toUpperCase() : "W";
};

const InviteDifferentAccountCard: React.FC<{
  inviteStatus: InviteStatusResponse;
  currentEmail: string | null;
  onSwitchAccount: () => void;
}> = ({ inviteStatus, currentEmail, onSwitchAccount }) => {
  const { t } = useTranslation();
  const workspaceName =
    inviteStatus.workspaceName || t("website.onboarding.invite.thisWorkspace");
  const roleName = formatInviteRoleName(
    inviteStatus.dataRoleName ||
      inviteStatus.data_role_name ||
      inviteStatus.workspaceRoleName ||
      inviteStatus.workspaceRoleKey ||
      inviteStatus.role,
    t,
  );
  const inviterName =
    inviteStatus.inviterName ||
    inviteStatus.inviterEmail ||
    t("website.onboarding.invite.teammate");

  return (
    <div className="w-full max-w-[900px] px-6">
      <div className="mx-auto flex w-full flex-col items-center text-center">
        <div className="mb-8 flex h-20 w-20 items-center justify-center rounded-[24px] border border-[#E0DFE8] bg-white text-[30px] font-bold text-[#1A162F] shadow-[0_4px_10px_rgba(26,22,47,0.08)]">
          {getWorkspaceInitial(workspaceName)}
        </div>

        <h1 className="max-w-[760px] text-[32px] font-bold leading-[1.15] text-[#1A162F] sm:text-[40px]">
          {t("website.onboarding.invite.joinWorkspaceTitle", {
            workspace: workspaceName,
          })}
        </h1>
        <p className="mt-7 max-w-[760px] text-[19px] leading-[1.45] text-[#676274] sm:text-[22px]">
          {t("website.onboarding.invite.singleSubtitle", {
            inviter: inviterName,
            article: getInviteRoleArticle(roleName),
            role: roleName,
          })}
        </p>
        <p className="mt-5 text-[18px] leading-snug text-[#8B8596] sm:text-[20px]">
          {t("website.onboarding.invite.sentTo")}{" "}
          <span className="font-bold text-[#1A162F]">
            {inviteStatus.invitedEmail}
          </span>
        </p>

        <div className="mt-8 flex w-full items-start gap-4 rounded-[18px] border border-red-200 bg-red-50 px-6 py-5 text-left text-red-700">
          <WarningCircle size={24} weight="bold" className="mt-0.5 shrink-0" />
          <div className="space-y-1 text-[18px] leading-[1.35] sm:text-[20px]">
            <p className="font-semibold">
              {t("website.onboarding.invite.differentAccountTitle")}
            </p>
            <p>
              {t("website.onboarding.invite.switchAccountDescription", {
                workspace: workspaceName,
              })}
            </p>
            {currentEmail ? (
              <p className="text-[15px] leading-relaxed text-red-600/80 sm:text-[16px]">
                {t("website.onboarding.invite.signedInAs")}{" "}
                <span className="font-semibold">{currentEmail}</span>.
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-8 w-full rounded-[20px] border border-[#E1E4EC] bg-white p-5 shadow-[0_8px_20px_rgba(26,22,47,0.08)]">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(260px,0.9fr)] md:items-center">
            <div className="flex min-w-0 items-center gap-5 text-left">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] border border-[#E0E3EC] bg-[#F4F5F8] text-[24px] font-bold text-[#1A162F]">
                {getWorkspaceInitial(workspaceName)}
              </div>
              <div className="min-w-0">
                <div className="truncate text-[20px] font-bold leading-tight text-[#1A162F]">
                  {workspaceName}
                </div>
                <div className="mt-2 truncate text-[18px] leading-tight text-[#6F697A]">
                  {t("website.onboarding.invite.roleLabel")} {roleName}
                </div>
              </div>
            </div>

            <button
              onClick={onSwitchAccount}
              className="flex min-h-[56px] w-full items-center justify-center rounded-[22px] bg-[#1A162F] px-6 text-center text-[18px] font-bold leading-tight text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#241F3D] sm:text-[20px]"
            >
              {t("website.onboarding.invite.logOutAndSwitch")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface StepProps {
  onNext: (payload?: {
    intent?: string;
    otherIntent?: string;
  }) => void | Promise<void>;
  onBack?: () => void;
  data: any;
  updateData: (data: any) => void;
}

interface PendingConnectionRequest {
  platformId: string;
  provider: string;
}

export const StepWelcome: React.FC<{
  onNext: () => void | Promise<void>;
  error: string | null;
  isLoading?: boolean;
  isPluginOnboarding: boolean;
}> = ({ onNext, error, isLoading = false, isPluginOnboarding }) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 700);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="mx-auto flex min-h-[calc(100vh-88px)] w-full max-w-[1140px] flex-col items-center justify-center px-6 py-12 lg:px-8">
      <div className="fixed left-6 top-6 z-[101]">
        <ProductBrandLogo
          coreLogoSrc={wordmark}
          className={
            isPluginOnboarding
              ? "h-auto w-[112px] sm:w-[128px]"
              : "h-9 w-auto sm:h-10"
          }
        />
      </div>

      <div className="flex w-full flex-col items-center">
        <div className="flex w-full flex-col items-center text-center">
          <div className="mb-10 flex w-full max-w-[700px] flex-col items-center">
            <h2 className="text-[32px] font-medium leading-[1.1] text-[#1A162F] sm:text-[36px] lg:text-[40px]">
              {t("website.onboarding.welcome.title")}
            </h2>
            <p className="mt-4 max-w-[600px] text-[15px] leading-relaxed text-[#666F8D] lg:text-[16px]">
              {t("website.onboarding.welcome.description")}
            </p>
          </div>

          <div className="flex w-full flex-col items-center justify-center">
            <div className="relative flex min-h-[400px] w-full items-center justify-center">
              <div className="pointer-events-none absolute inset-x-10 top-16 h-40 origin-center scale-[1.7] rounded-full bg-[radial-gradient(circle,_rgba(112,127,255,0.18)_0%,_rgba(112,127,255,0)_72%)] blur-3xl" />
              <div className="pointer-events-none absolute inset-x-14 bottom-10 h-28 origin-center scale-[1.7] rounded-full bg-[radial-gradient(circle,_rgba(26,22,47,0.10)_0%,_rgba(26,22,47,0)_72%)] blur-2xl" />

              <div className="relative flex w-full max-w-[450px] origin-center perspective-[1000px] flex-col items-center justify-center scale-[1.1]">
                <div
                  className={`relative mt-2 h-[380px] w-[248px] rounded-2xl animate-in fade-in slide-in-from-bottom-12 zoom-in-90 cursor-default transition-all duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] ${isLoaded ? "group hover:-translate-y-4 hover:scale-[1.02] hover:-rotate-2" : ""}`}
                >
                  <img
                    src={idCard}
                    className="absolute inset-0 h-full w-full object-contain drop-shadow-[0_24px_40px_rgba(26,22,47,0.16)] transition-all duration-700 ease-out group-hover:drop-shadow-[0_40px_60px_rgba(110,91,255,0.35)]"
                    alt="ID Card"
                  />

                  <div className="absolute left-1/2 top-[128px] z-10 flex w-[190px] -translate-x-1/2 flex-col items-center px-3 pb-2 text-center">
                    <div className="relative mb-4 flex h-[40px] w-[40px] shrink-0 items-center justify-center overflow-visible">
                      <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#272729] shadow-sm ring-2 ring-white/10 transition-transform duration-500 ease-out group-hover:scale-110 group-hover:shadow-[0_0_15px_rgba(110,91,255,0.4)]">
                        <img
                          src={BUSTLY_AVATAR_SRC}
                          alt={isPluginOnboarding ? "BizSidekick" : "Bustly"}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      <div className="absolute left-[110%] top-[-24%] z-[60] rounded-xl rounded-bl-sm border border-[#D9D4F4] bg-[#F6F3FF]/95 px-2.5 py-1.5 shadow-[0_10px_20px_rgba(26,22,47,0.12)] backdrop-blur-sm animate-in fade-in slide-in-from-bottom-2 duration-700 delay-300 transition-all group-hover:-translate-y-1 group-hover:rotate-2">
                        <div className="flex items-start gap-1.5">
                          <span className="relative mt-1 flex h-1.5 w-1.5 shrink-0">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#6E5BFF] opacity-70" />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#1A162F]" />
                          </span>
                          <div className="whitespace-nowrap text-left text-[8px] font-medium leading-[1.3] text-[#1A162F]">
                            <p>
                              {t(
                                isPluginOnboarding
                                  ? "website.onboarding.welcome.pluginGreeting"
                                  : "website.onboarding.welcome.greeting",
                              )}
                            </p>
                            <p>
                              {t("website.onboarding.welcome.avatarSubtitle")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 flex w-full flex-col items-center text-center">
                      <h3 className="flex min-h-[22px] w-full items-center justify-center truncate whitespace-nowrap px-1 text-[16px] font-medium leading-[1.1] text-white">
                        {isPluginOnboarding ? "BizSidekick" : "Bustly"}
                      </h3>
                      <p className="mt-2 text-[12px] font-medium leading-none text-[#B8BDD4]">
                        -
                      </p>
                      <p className="mt-2 text-[12px] font-medium leading-[1.1] text-white">
                        {t("website.onboarding.welcome.avatarRole")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center">
            <button
              onClick={() => {
                void onNext();
              }}
              disabled={isLoading}
              className={clsx(
                "flex items-center gap-2 rounded-full px-8 py-3.5 text-[15px] font-bold text-white transition-all",
                isLoading
                  ? "cursor-not-allowed bg-gray-200 text-gray-500 shadow-none"
                  : "bg-[#1A162F] shadow-lg hover:-translate-y-0.5 hover:bg-[#1A162F]/90 hover:shadow-xl",
              )}
            >
              {isLoading ? (
                <CircleNotch size={18} weight="bold" className="animate-spin" />
              ) : null}
              {t(
                isPluginOnboarding
                  ? "website.onboarding.welcome.pluginCta"
                  : "website.onboarding.welcome.cta",
              )}
              {!isLoading ? <ArrowRight size={18} weight="bold" /> : null}
            </button>

            {error ? (
              <div role="alert" className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const StepIntent: React.FC<
  StepProps & { isLoading?: boolean; error?: string | null }
> = ({ onNext, onBack, data, updateData, isLoading = false, error = null }) => {
  const { t } = useTranslation();
  const intentOptions = useMemo(() => buildIntentOptions(t), [t]);
  const [selectedIntent, setSelectedIntent] = useState(data?.intent || null);
  const [otherText, setOtherText] = useState(data?.otherIntent || "");

  const handleSelect = (id: string) => {
    setSelectedIntent(id);
    updateData({ intent: id });
  };

  const canContinue = Boolean(
    selectedIntent && (selectedIntent !== "other" || otherText.trim() !== ""),
  );

  return (
    <div className="mx-auto flex min-h-[calc(100vh-88px)] w-full max-w-[1140px] flex-col items-center justify-center px-6 py-12 lg:px-8">
      <div className="flex w-full flex-col items-center">
        <div className="flex w-full flex-col items-center text-left">
          <div className="mb-10 flex w-full max-w-[700px] flex-col items-center text-center">
            <h2 className="text-[32px] font-medium leading-[1.1] text-[#1A162F] sm:text-[36px] lg:text-[40px]">
              {t("website.onboarding.intent.title")}
            </h2>
            <p className="mt-4 max-w-[600px] text-[15px] leading-relaxed text-[#666F8D] lg:text-[16px]">
              {t("website.onboarding.intent.description")}
            </p>
          </div>

          <div className="flex w-full max-w-[540px] flex-col items-center">
            <V13AssistantHint
              lines={[
                t("website.onboarding.intent.hintLine1"),
                t("website.onboarding.intent.hintLine2"),
              ]}
            />

            <div className="flex w-full flex-col gap-3">
              {intentOptions.map((option) => {
                const isSelected = selectedIntent === option.id;
                const isOther = option.id === "other";

                return (
                  <div
                    key={option.id}
                    onClick={() => handleSelect(option.id)}
                    className={clsx(
                      "flex w-full cursor-pointer items-center gap-3 rounded-[18px] border px-4 py-3.5 text-left transition-all duration-300",
                      isSelected
                        ? "border-[#D9DFEC] bg-white shadow-[0_8px_22px_rgba(26,22,47,0.06)]"
                        : "border-[#E6E8F0] bg-white shadow-[0_4px_14px_rgba(26,22,47,0.04)] hover:border-[#1A162F] hover:shadow-[0_8px_22px_rgba(26,22,47,0.07)]",
                    )}
                  >
                    <div
                      className={clsx(
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                        isSelected ? "border-[#1A162F]" : "border-gray-300",
                      )}
                    >
                      {isSelected ? (
                        <div className="h-2.5 w-2.5 rounded-full bg-[#1A162F]" />
                      ) : null}
                    </div>

                    <div className="relative flex min-w-0 flex-1 items-center">
                      {isOther ? (
                        <>
                          <span
                            className={clsx(
                              "pointer-events-none absolute left-0 block text-[15px] font-bold transition-all duration-300",
                              isSelected ? "text-[#1A162F]" : "text-[#1A162F]",
                              isSelected || otherText
                                ? "opacity-0 -translate-y-2"
                                : "opacity-100 translate-y-0",
                            )}
                          >
                            {option.label}
                          </span>
                          <input
                            type="text"
                            placeholder={
                              isSelected
                                ? t(
                                    "website.onboarding.intent.otherPlaceholder",
                                  )
                                : ""
                            }
                            value={otherText}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelect(option.id);
                            }}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setOtherText(nextValue);
                              updateData({ otherIntent: nextValue });
                              if (!isSelected) {
                                handleSelect(option.id);
                              }
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" && canContinue) {
                                void onNext({
                                  intent: selectedIntent || option.id,
                                  otherIntent: event.currentTarget.value.trim(),
                                });
                              }
                            }}
                            className={clsx(
                              "w-full appearance-none border-0 bg-transparent text-[15px] font-bold text-text-main caret-[#1A162F] shadow-none ring-0 transition-all duration-300 placeholder:font-normal placeholder:text-gray-400 outline-none focus:border-0 focus:outline-none focus:ring-0 focus:shadow-none focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none",
                              !isSelected && !otherText
                                ? "opacity-0 translate-y-2 pointer-events-none"
                                : "opacity-100 translate-y-0",
                            )}
                            style={{
                              WebkitAppearance: "none",
                              appearance: "none",
                              outline: "none",
                              boxShadow: "none",
                              border: "0",
                              borderRadius: 0,
                            }}
                          />
                        </>
                      ) : (
                        <div className="flex flex-col">
                          <span className="block text-[15px] font-bold text-[#1A162F]">
                            {option.label}
                          </span>
                          {option.desc ? (
                            <span
                              className={clsx(
                                "text-[13px] mt-0.5",
                                isSelected
                                  ? "text-[#666F8D]"
                                  : "text-[#8A93B2]",
                              )}
                            >
                              {option.desc}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex w-full items-center justify-between">
              <button
                onClick={onBack}
                className="flex min-w-[100px] items-center justify-center rounded-full border border-gray-200 bg-transparent px-6 py-3 text-[14px] font-bold text-[#1A162F] shadow-sm transition-all hover:border-gray-300 hover:bg-white hover:shadow"
              >
                {t("website.onboarding.common.back")}
              </button>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    void onNext({
                      intent: "skipped",
                      otherIntent: "",
                    });
                  }}
                  disabled={isLoading}
                  className="flex min-w-[100px] items-center justify-center rounded-full border border-transparent bg-transparent px-6 py-3 text-[14px] font-bold text-[#666F8D] transition-all hover:text-[#1A162F] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("website.onboarding.common.skip")}
                </button>
                <button
                  onClick={() => {
                    void onNext({
                      intent: selectedIntent || "",
                      otherIntent: otherText.trim(),
                    });
                  }}
                  disabled={!canContinue || isLoading}
                  className={clsx(
                    "flex items-center gap-2 rounded-full px-8 py-3 text-[15px] font-bold transition-all shadow-lg",
                    canContinue && !isLoading
                      ? "bg-[#1A162F] text-white hover:-translate-y-0.5 hover:bg-[#1A162F]/90 hover:shadow-xl"
                      : "cursor-not-allowed bg-gray-100 text-gray-400 shadow-none",
                  )}
                >
                  {isLoading ? (
                    <CircleNotch
                      size={18}
                      weight="bold"
                      className="animate-spin"
                    />
                  ) : null}
                  {t("website.onboarding.intent.setPriority")}
                  {!isLoading ? <ArrowRight size={18} weight="bold" /> : null}
                </button>
              </div>
            </div>

            {error ? (
              <div className="mt-4 w-full rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const StepConnect: React.FC<
  StepProps & {
    isLoading?: boolean;
    pendingConnectionRequest: PendingConnectionRequest | null;
    setPendingConnectionRequest: React.Dispatch<
      React.SetStateAction<PendingConnectionRequest | null>
    >;
    workspaceReady?: boolean;
    workspaceError?: string | null;
    workspaceIntegrations: UseWorkspaceIntegrationsReturn;
    requiredConnectionCount: number;
    connectionRequirementBasis: OnboardingConnectionRequirementBasis;
    isPluginOnboarding: boolean;
  }
> = ({
  onNext,
  onBack,
  data,
  updateData,
  isLoading = false,
  pendingConnectionRequest,
  setPendingConnectionRequest,
  workspaceReady = true,
  workspaceError = null,
  workspaceIntegrations,
  requiredConnectionCount,
  connectionRequirementBasis,
  isPluginOnboarding,
}) => {
  const { t } = useTranslation();
  const [connectionReview, setConnectionReview] = useState<{ platformId: string; provider: string } | null>(null);
  const { showToast, showActionToast } = useToast();
  const { showDialog } = useDialog();
  const [
    googleAdsAccountSelectionConnectionId,
    setGoogleAdsAccountSelectionConnectionId,
  ] = useState<string | null>(null);
  const [
    googleAdsAccountSelectionDiscovery,
    setGoogleAdsAccountSelectionDiscovery,
  ] = useState<{
    response: GoogleAdsAccountsResponse;
    workspaceId: string | null;
    connectionId: string;
  } | null>(null);
  const [googleAdsBrandOptions, setGoogleAdsBrandOptions] = useState<BrandSelectOption[]>([]);
  const [onboardingResourceSelection, setOnboardingResourceSelection] =
    useState<{
      displayProvider: string;
      provider: string;
      workspaceIntegrationId: number;
      nangoConnectionId: string;
      resources: IntegrationResource[];
      projectId: string | null;
      isLoading: boolean;
    } | null>(null);
  const [isSavingOnboardingResources, setIsSavingOnboardingResources] =
    useState(false);
  const onboardingResourceSelectionRequestIdRef = useRef(0);
  const browseCategories = useMemo(
    () => {
      const connectionCategories = getOnboardingConnectionCategories(
        isPluginOnboarding,
      );
      return getVisibleOnboardingConnectionCategories(
        isPluginOnboarding ? PLUGIN_ONBOARDING_EXCLUDED_CATEGORY_IDS : [],
        connectionCategories,
      );
    },
    [isPluginOnboarding],
  );
  const platformOptions = useMemo(
    () => buildOnboardingIntegrationOptions(browseCategories),
    [browseCategories],
  );
  const pluginConnectionInstances = useMemo(
    () =>
      resolveOnboardingConnectionInstances(
        workspaceIntegrations.allIntegrations ?? [],
      ),
    [workspaceIntegrations.allIntegrations],
  );
  const onboardingConnectionUsage = useMemo(
    () =>
      resolveOnboardingConnectionUsage(
        pluginConnectionInstances,
        isPluginOnboarding,
      ),
    [isPluginOnboarding, pluginConnectionInstances],
  );
  const onboardingConnectionLimits = isPluginOnboarding
    ? PLUGIN_PLAN_LIMITS
    : BUSTLY_CORE_ONBOARDING_LIMITS;
  const [selectedCategory, setSelectedCategory] =
    useState<OnboardingConnectionBrowseCategoryId>("store_ops");
  const connectingTool = data.storeConnectionPlatform || null;
  const shopifyReturnLocation = useLocation();
  const hasShopifyReturn = isInlineShopifyReturn(shopifyReturnLocation.search);
  const [shopifyReturnState, setShopifyReturnState] = useState<"idle" | "checking" | "error">("idle");
  const [shopifyEntryAttempt, setShopifyEntryAttempt] = useState(0);
  const [shopifyEntryState, setShopifyEntryState] = useState<"idle" | "checking" | "error">("idle");
  const [disconnectingPlatformId, setDisconnectingPlatformId] = useState<
    string | null
  >(null);
  const [expandedPluginTool, setExpandedPluginTool] = useState<string | null>(
    null,
  );
  const [collapsedPluginTools, setCollapsedPluginTools] = useState<
    Set<string>
  >(new Set());
  const [manuallyExpandedTools, setManuallyExpandedTools] = useState<
    Set<string>
  >(new Set());
  const [disconnectingConnectionId, setDisconnectingConnectionId] = useState<
    string | null
  >(null);
  const disconnectConfirmationKeysRef = useRef<Set<string>>(new Set());
  const [savingLabelConnectionId, setSavingLabelConnectionId] = useState<
    string | null
  >(null);
  const [editingLabelInstance, setEditingLabelInstance] =
    useState<OnboardingConnectionInstance | null>(null);
  const [editingLabelValue, setEditingLabelValue] = useState("");
  const [connectionLabelDrafts, setConnectionLabelDrafts] = useState<
    Record<string, string>
  >({});
  const [storeConnectionLabel, setStoreConnectionLabel] = useState("");
  const storeUrl = data.storeConnectionStoreUrl || "";
  const showConnectionFeedbackToast = (error: unknown, platformId: string) => {
    const feedback = resolveOnboardingConnectionFeedback({
      error,
      fallbackMessage: t("website.onboarding.connect.connectFailed", {
        platform: platformId,
      }),
      notCompletedMessage: t(
        "website.onboarding.connect.connectionNotCompleted",
      ),
      useInformationalExistingConnectionFeedback: isPluginOnboarding,
    });

    if (feedback.duration) {
      showActionToast({
        message: feedback.message,
        type: feedback.type,
        duration: feedback.duration,
      });
      return;
    }

    showToast(feedback.message, feedback.type);
  };
  const setStoreUrl = useCallback(
    (value: string) => {
      updateData({ storeConnectionStoreUrl: value });
    },
    [updateData],
  );
  const [manualCredentialPlatformId, setManualCredentialPlatformId] = useState<
    string | null
  >(null);
  const [manualCredentialApiKey, setManualCredentialApiKey] = useState("");
  const [manualCredentialStoreUrl, setManualCredentialStoreUrl] = useState("");
  const [manualCredentialAccountLabel, setManualCredentialAccountLabel] =
    useState("");
  const [credentialTool, setCredentialTool] =
    useState<IntegrationCredentialTool | null>(null);
  const [credentialValues, setCredentialValues] = useState(() =>
    createIntegrationCredentialValues(),
  );
  const [syncingTools, setSyncingTools] = useState<Set<string>>(new Set());
  const bigCommerceStoreHash = data.storeConnectionStoreHash || "";
  const setBigCommerceStoreHash = useCallback(
    (value: string) => {
      updateData({ storeConnectionStoreHash: value });
    },
    [updateData],
  );
  const bigCommerceAccountUuid = data.storeConnectionAccountUuid || "";
  const setBigCommerceAccountUuid = useCallback(
    (value: string) => {
      updateData({ storeConnectionAccountUuid: value });
    },
    [updateData],
  );
  const urlConnectionInFlightRef = useRef(false);
  const manualCredentialConnectionInFlightRef = useRef(false);
  const credentialConnectionInFlightRef = useRef(false);
  const connectedIntegrationsRef = useRef<string[]>(
    data.connectedIntegrations || [],
  );
  const trialRequirementsRef = useRef(
    data.connectedIntegrationTrialRequirements || {},
  );
  const pendingEligibilityPayloadRef = useRef<
    Record<string, ConnectRestrictionPayload | undefined>
  >({});
  const lastServerConnectionSnapshotRef = useRef<string | null>(null);
  const googleAdsAccountSelectionSavedRef = useRef(false);
  const isStepConnectMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isStepConnectMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    connectedIntegrationsRef.current = data.connectedIntegrations || [];
  }, [data.connectedIntegrations]);

  useEffect(() => {
    trialRequirementsRef.current =
      data.connectedIntegrationTrialRequirements || {};
  }, [data.connectedIntegrationTrialRequirements]);

  const resetConnectionForm = useCallback(() => {
    setStoreConnectionLabel("");
    updateData({
      storeConnectionPlatform: null,
      storeConnectionStoreUrl: "",
      storeConnectionStoreHash: "",
      storeConnectionAccountUuid: "",
    });
  }, [updateData]);

  useEffect(() => {
    if (hasShopifyReturn && shopifyReturnState !== "idle" && connectingTool === "Shopify") resetConnectionForm();
  }, [hasShopifyReturn, shopifyReturnState, connectingTool, resetConnectionForm]);

  const resetManualCredentialForm = useCallback(() => {
    setManualCredentialPlatformId(null);
    setManualCredentialApiKey("");
    setManualCredentialStoreUrl("");
    setManualCredentialAccountLabel("");
  }, []);

  const resetIntegrationCredentialForm = useCallback(() => {
    setCredentialTool(null);
    setCredentialValues(createIntegrationCredentialValues());
  }, []);

  const syncConnectedIntegrationMetadata = async (
    platformId: string,
    provider: string,
    expectedConnectionIdentity?: string,
  ) => {
    const normalizedProvider = normalizeIntegrationProvider(provider);
    const baselineConnectionCount = resolveOnboardingConnectionInstances(
      workspaceIntegrations.allIntegrations ?? [],
    ).filter(
      (instance) =>
        instance.isValidConnection && instance.provider === normalizedProvider,
    ).length;
    const normalizedExpectedIdentity = expectedConnectionIdentity
      ?.trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/[/?#]+$/, "");
    const integrations = await refreshOnboardingConnectionSnapshot({
      refresh: workspaceIntegrations.refresh,
      isSnapshotReady: (snapshot) => {
        const instances = resolveOnboardingConnectionInstances(snapshot).filter(
          (instance) =>
            instance.isValidConnection &&
            instance.provider === normalizedProvider,
        );

        if (normalizedExpectedIdentity) {
          return instances.some(
            (instance) =>
              instance.connectionId?.trim().toLowerCase() === normalizedExpectedIdentity ||
              instance.externalIdentity === normalizedExpectedIdentity ||
              instance.shopDomain === normalizedExpectedIdentity ||
              instance.accountName === normalizedExpectedIdentity,
          );
        }

        return instances.length > baselineConnectionCount;
      },
    });
    const providerInstances = resolveOnboardingConnectionInstances(integrations)
      .filter(
        (instance) =>
          instance.isValidConnection &&
          instance.provider === normalizedProvider,
      );
    const matchingInstance = normalizedExpectedIdentity
      ? [...providerInstances].reverse().find((instance) =>
          [
            instance.connectionId,
            instance.externalIdentity,
            instance.shopDomain,
            instance.accountName,
          ].some(
            (candidate) =>
              candidate?.trim().toLowerCase() === normalizedExpectedIdentity,
          ),
        )
      : [...providerInstances].reverse()[0];
    const refreshedIntegration = matchingInstance
      ? integrations[matchingInstance.sourceIntegrationIndex] || null
      : [...integrations]
          .reverse()
          .find((integration) =>
            matchesIntegrationProvider(integration.provider, provider),
          ) || null;
    const nextConnectedIntegrations = createUpdatedConnectionList(
      connectedIntegrationsRef.current,
      platformId,
    );
    connectedIntegrationsRef.current = nextConnectedIntegrations;

    const nextData: Record<string, unknown> = {
      connectedIntegrations: nextConnectedIntegrations,
    };

    if (isStoreOpsProvider(provider)) {
      const { storeName, storeLogoUrl } = resolveConnectedStoreProfile(
        integrations,
        provider,
      );
      nextData.connectedStoreName = data.connectedStoreName || storeName;
      nextData.connectedStoreLogoUrl =
        data.connectedStoreLogoUrl || storeLogoUrl;
    }

    updateData(nextData);

    const expansionTarget = resolveOnboardingConnectionExpansionTarget({
      platformId,
      isConnected: Boolean(refreshedIntegration?.connected),
      platformOptions,
    });
    if (expansionTarget && isStepConnectMountedRef.current) {
      setSelectedCategory(expansionTarget.categoryId);
      setExpandedPluginTool(expansionTarget.tool);
    }

    return refreshedIntegration;
  };

  useEffect(() => {
    if (
      !data.workspaceId ||
      !workspaceIntegrations.hasFetchedCurrentWorkspace
    ) {
      return;
    }

    const integrations = workspaceIntegrations.allIntegrations ?? [];
    const connectedOptions = getConnectedOnboardingOptions(
      integrations,
      platformOptions,
    );
    const connectedToolNames = connectedOptions.map((option) => option.id);
    const firstConnectedStore = connectedOptions.find(
      (option) => isStoreOpsProvider(option.provider),
    );
    const connectedStoreProfile = firstConnectedStore
      ? resolveConnectedStoreProfile(integrations, firstConnectedStore.provider)
      : { storeName: "", storeLogoUrl: null };
    const snapshotKey = JSON.stringify({
      connectedToolNames,
      connectedStoreName: connectedStoreProfile.storeName || "",
      connectedStoreLogoUrl: connectedStoreProfile.storeLogoUrl || null,
    });

    if (lastServerConnectionSnapshotRef.current === snapshotKey) {
      return;
    }

    lastServerConnectionSnapshotRef.current = snapshotKey;
    connectedIntegrationsRef.current = connectedToolNames;
    updateData({
      connectedIntegrations: connectedToolNames,
      connectedStoreName: connectedStoreProfile.storeName || "",
      connectedStoreLogoUrl: connectedStoreProfile.storeLogoUrl || null,
    });
  }, [
    data.workspaceId,
    platformOptions,
    updateData,
    workspaceIntegrations.allIntegrations,
    workspaceIntegrations.hasFetchedCurrentWorkspace,
  ]);

  const buildEligibilityPayloadFromIntegration = (
    integration?: {
      restrictionState?: StoreTrialEligibilityState | null;
      eligibilityState?: StoreTrialEligibilityState | null;
      blockConnect?: boolean | null;
      upgradeRequired?: boolean | null;
      allowsFreshTrial?: boolean | null;
      message?: string | null;
      eligibilityMessage?: string | null;
      currentWorkspaceId?: string | null;
      currentWorkspaceName?: string | null;
      trialRemainingDays?: number | null;
    } | null,
  ) => {
    if (!integration) {
      return undefined;
    }

    return {
      restrictionState: integration.restrictionState,
      eligibilityState: integration.eligibilityState,
      blockConnect: integration.blockConnect ?? undefined,
      upgradeRequired: integration.upgradeRequired ?? undefined,
      allowsFreshTrial: integration.allowsFreshTrial ?? undefined,
      message: integration.message ?? integration.eligibilityMessage,
      currentWorkspaceId: integration.currentWorkspaceId ?? undefined,
      currentWorkspaceName: integration.currentWorkspaceName ?? undefined,
      trialRemainingDays: integration.trialRemainingDays ?? undefined,
    };
  };

  const applyEligibilityFromConnection = (
    platformId: string,
    result?: ConnectRestrictionPayload,
    rawStoreUrl = storeUrl,
  ) => {
    const platform = platformOptions.find((option) => option.id === platformId);
    if (!platform) {
      return false;
    }

    const normalizedRestriction = normalizeConnectRestriction(result);
    const effectiveRestrictionState =
      normalizedRestriction.restrictionState ??
      (connectedIntegrationsRef.current.includes(platformId)
        ? inferConnectedTrialRestrictionState(
            normalizedRestriction,
            data.workspaceId,
          )
        : null);
    const restriction = {
      ...normalizedRestriction,
      restrictionState: effectiveRestrictionState,
    };
    const isFreeTrialUnavailable =
      isFreeTrialUnavailableRestriction(restriction);
    const nextTrialRequirements = updateConnectedIntegrationTrialRequirements(
      trialRequirementsRef.current,
      platformId,
      {
        restrictionState: restriction.restrictionState,
        blockConnect: restriction.blockConnect,
        allowsFreshTrial: restriction.allowsFreshTrial ?? undefined,
        isPluginOnboarding,
      },
    );
    trialRequirementsRef.current = nextTrialRequirements;

    if (
      isFreeTrialUnavailable
    ) {
      updateData({
        storeConnectionPlatform: null,
        storeConnectionStoreUrl: "",
        storeConnectionStoreHash: "",
        storeConnectionAccountUuid: "",
        connectedIntegrationTrialRequirements: nextTrialRequirements,
        trialEligibilityState: resolveAggregatedOnboardingTrialEligibilityState(
          nextTrialRequirements,
        ),
        connectedStoreName:
          data.connectedStoreName ||
          buildFallbackStoreName(platformId, rawStoreUrl),
      });
      return true;
    }

    updateData({
      connectedIntegrationTrialRequirements: nextTrialRequirements,
      trialEligibilityState: resolveAggregatedOnboardingTrialEligibilityState(
        nextTrialRequirements,
      ),
    });
    return false;
  };
  const shouldSuppressConnectionSuccessToastAfterEligibility = (
    result?: (ConnectRestrictionPayload & { success?: boolean | null }) | null,
  ) => {
    const restriction = normalizeConnectRestriction(result);
    return result?.success === false || restriction.blockConnect === true;
  };

  useEffect(() => {
    const nextTrialRequirements =
      resolveConnectedIntegrationTrialRequirements(pluginConnectionInstances);
    const previousTrialRequirements = trialRequirementsRef.current;

    if (
      JSON.stringify(previousTrialRequirements) ===
      JSON.stringify(nextTrialRequirements)
    ) {
      return;
    }

    trialRequirementsRef.current = nextTrialRequirements;
    updateData({
      connectedIntegrationTrialRequirements: nextTrialRequirements,
      trialEligibilityState: resolveAggregatedOnboardingTrialEligibilityState(
        nextTrialRequirements,
      ),
    });
  }, [pluginConnectionInstances, updateData]);

  const getPluginConnectionLimitMessage = useCallback(
    (state: PluginConnectionAdditionState) => {
      if (state.reason === "store_limit_reached") {
        return t("website.onboarding.connect.limits.store", {
          limit: state.limit,
        });
      }
      if (state.reason === "integration_group_limit_reached") {
        return t("website.onboarding.connect.limits.other", {
          limit: state.limit,
        });
      }
      return t("website.onboarding.connect.limits.total", {
        limit: state.limit,
      });
    },
    [t],
  );

  const canStartOnboardingConnection = useCallback(
    (provider: string, notify = true) => {
      const additionState = resolveOnboardingConnectionAdditionState({
        instances: pluginConnectionInstances,
        provider,
        isPluginOnboarding,
      });
      if (additionState.allowed) {
        return true;
      }

      if (notify) {
        showToast(getPluginConnectionLimitMessage(additionState), "error");
      }
      return false;
    },
    [
      getPluginConnectionLimitMessage,
      isPluginOnboarding,
      pluginConnectionInstances,
      showToast,
    ],
  );

  const startConnectionFlow = async (
    platformId: string,
    provider: string,
    options?: {
      skipSyncingGuard?: boolean;
      connectionConfig?: Record<string, unknown>;
    },
  ) => {
    if (syncingTools.has(platformId) && !options?.skipSyncingGuard) {
      return;
    }

    if (!canStartOnboardingConnection(provider)) {
      return;
    }

    const connectEntryPoint = resolveIntegrationConnectEntryPoint(platformId, {
      hasLegacyStoreUrlInput: Boolean(STORE_URL_INPUT_CONFIG[platformId]),
      hasLegacyCredentialInput: isIntegrationCredentialTool(platformId),
    });

    if (connectEntryPoint.kind === "legacy-store-url") {
      setStoreConnectionLabel("");
      updateData({
        storeConnectionPlatform: platformId,
        storeConnectionStoreUrl: "",
        storeConnectionStoreHash: "",
        storeConnectionAccountUuid: "",
      });
      return;
    }

    if (
      connectEntryPoint.kind === "legacy-credential" &&
      isIntegrationCredentialTool(platformId) &&
      !options?.connectionConfig
    ) {
      setCredentialTool(platformId);
      setCredentialValues(createIntegrationCredentialValues(platformId));
      return;
    }

    if (requiresManualCredentialInput(platformId) && !options?.connectionConfig) {
      setManualCredentialPlatformId(platformId);
      setManualCredentialApiKey("");
      setManualCredentialStoreUrl("");
      setManualCredentialAccountLabel("");
      return;
    }

    const tracksTrialEligibility = true;
    const clearSyncingState = () => {
      setSyncingTools((previous) => {
        const next = new Set(previous);
        next.delete(platformId);
        return next;
      });
      delete pendingEligibilityPayloadRef.current[platformId];
    };
    const resetCredentialFormAfterSuccess = () => {
      if (requiresManualCredentialInput(platformId)) {
        resetManualCredentialForm();
      }
      if (isIntegrationCredentialTool(platformId)) {
        resetIntegrationCredentialForm();
      }
    };
    const handleNonStoreRestriction = (
      payload?: (ConnectRestrictionPayload & { success?: boolean }) | null,
    ) => {
      const restriction = normalizeConnectRestriction(payload);
      if (
        payload?.success !== false &&
        !restriction.blockConnect &&
        !(payload?.success !== true && restriction.upgradeRequired)
      ) {
        return false;
      }

      showConnectionFeedbackToast(restriction.message, platformId);
      return true;
    };

    if (isStoreOpsProvider(provider)) {
      const submittedStoreIdentity =
        resolveOnboardingStoreConnectionIdentity(
          provider,
          options?.connectionConfig || storeUrl,
        );
      if (
        hasDuplicateOnboardingStoreConnection(
          pluginConnectionInstances,
          provider,
          submittedStoreIdentity,
        )
      ) {
        showToast(
          t("website.onboarding.connect.duplicateStoreConnection", {
            store: submittedStoreIdentity,
          }),
          "info",
        );
        if (requiresManualCredentialInput(platformId)) {
          resetManualCredentialForm();
        }
        if (isIntegrationCredentialTool(platformId)) {
          resetIntegrationCredentialForm();
        }
        return;
      }
    }

    try {
      setSyncingTools((prev) => new Set(prev).add(platformId));
      const result = await workspaceIntegrations.connect(
        provider,
        {
          connection_scene: "onboarding",
          ...(options?.connectionConfig || {}),
        },
        {
          onSuccess: async () => {
            const refreshedIntegration = await syncConnectedIntegrationMetadata(
              platformId,
              provider,
              isStoreOpsProvider(provider)
                ? resolveOnboardingStoreConnectionIdentity(
                    provider,
                    options?.connectionConfig || storeUrl,
                  )
                : undefined,
            );
            if (["google-analytics", "google-search-console"].includes(provider)) {
              // Nango can invoke the success callback before connect() returns
              // its payload.  Do not read the outer result here; the
              // post-connect branch below owns GA/GSC resource selection with
              // the returned Nango connection id.
              clearSyncingState();
              resetCredentialFormAfterSuccess();
              return;
            }
            const refreshedEligibilityPayload = tracksTrialEligibility
              ? buildEligibilityPayloadFromIntegration(refreshedIntegration)
              : undefined;
            const eligibilityPayload = mergeEligibilityPayload(
              refreshedEligibilityPayload,
              pendingEligibilityPayloadRef.current[platformId],
            );
            clearSyncingState();
            resetCredentialFormAfterSuccess();
            const eligibilityApplied =
              tracksTrialEligibility &&
              applyEligibilityFromConnection(
                platformId,
                eligibilityPayload as any,
              );
            if (
              eligibilityApplied &&
              shouldSuppressConnectionSuccessToastAfterEligibility(
                eligibilityPayload as any,
              )
            ) {
              return;
            }
            showToast(
              t("website.onboarding.connect.connectedSuccess", {
                platform: platformId,
              }),
              "success",
            );
          },
          onError: (message: string) => {
            clearSyncingState();
            showConnectionFeedbackToast(message, platformId);
          },
          onRestriction: (restriction) => {
            clearSyncingState();
            if (
              tracksTrialEligibility &&
              applyEligibilityFromConnection(platformId, restriction)
            ) {
              return;
            }
            handleNonStoreRestriction(restriction);
          },
        },
      );
      pendingEligibilityPayloadRef.current[platformId] = result;

      const manualSubmittedStoreUrl =
        typeof options?.connectionConfig?.storeUrl === "string"
          ? options.connectionConfig.storeUrl
          : typeof options?.connectionConfig?.storeURL === "string"
            ? options.connectionConfig.storeURL
            : typeof options?.connectionConfig?.storeDomain === "string"
              ? options.connectionConfig.storeDomain
              : storeUrl;

      const resultEligibilityApplied =
        tracksTrialEligibility &&
        applyEligibilityFromConnection(
          platformId,
          result,
          manualSubmittedStoreUrl,
        );
      if (
        resultEligibilityApplied &&
        shouldSuppressConnectionSuccessToastAfterEligibility(result)
      ) {
        clearSyncingState();
        resetCredentialFormAfterSuccess();
        return;
      }

      if (handleNonStoreRestriction(result)) {
        clearSyncingState();
        return;
      }

      // Google Ads can offer an explicit CUSTOMER selection after OAuth.
      // Keep the metadata check for connector compatibility, but only open the
      // optional picker when discovery returns selectable child resources.
      const requiresGoogleAdsCustomerSelection =
        provider === "google-ads" && Boolean(result?.connectionId);
      if (result?.requiresAccountSelection || requiresGoogleAdsCustomerSelection) {
        clearSyncingState();
        if (!result.connectionId) {
          showToast(
            t("website.onboarding.connect.connectFailed", {
              platform: platformId,
            }),
            "error",
          );
          return;
        }
        if (provider === "google-ads") {
          const completeGoogleAdsConnectionWithoutSelection = () => {
            resetCredentialFormAfterSuccess();
            showToast(
              t("website.onboarding.connect.connectedSuccess", {
                platform: platformId,
              }),
              "success",
            );
          };
          try {
            const accountSelection = await authService.getNangoGoogleAdsAccounts({
              connectionId: result.connectionId,
              axiosRequestConfig: data.workspaceId
                ? { headers: { "X-Workspace-Id": data.workspaceId } }
                : undefined,
            });
            const eligibleCustomerIds = Array.from(
              new Set(
                (accountSelection.eligibleCustomerIds?.length
                  ? accountSelection.eligibleCustomerIds
                  : buildGoogleAdsRows(accountSelection.accounts)
                      .filter((account) => account.eligible)
                      .map((account) => account.id)
                )
                  .map((customerId) => normalizeCustomerId(customerId))
                  .filter(Boolean),
              ),
            );
            if (!accountSelection.hasIntegration || !eligibleCustomerIds.length) {
              completeGoogleAdsConnectionWithoutSelection();
              return;
            }
            setGoogleAdsAccountSelectionDiscovery({
              response: {
                ...accountSelection,
                eligibleCustomerIds,
              },
              workspaceId: data.workspaceId || null,
              connectionId: String(result.connectionId),
            });
          } catch {
            // Keep the OAuth connection, but do not report success before
            // account discovery has completed. Open the picker without an
            // initial response so its retryable loader can recover in place.
            setGoogleAdsAccountSelectionDiscovery(null);
            showToast(
              t("website.onboarding.connect.connectFailed", {
                platform: platformId,
              }),
              "error",
            );
          }
        }
        // Onboarding only lets the user choose Google Ads customers. The
        // selected customers are saved into the workspace default Business
        // group, so load the latest project list before opening the picker.
        // Plugin onboarding only selects connected accounts, so it must not
        // load Business group inventory.
        if (!isPluginOnboarding && data.workspaceId) {
          try {
            const { projectService } = await import("@common/api/projectService");
            const brands = await projectService.listBrands(data.workspaceId!);
            setGoogleAdsBrandOptions(
              brands.map((brand) => ({
                id: brand.id,
                name: brand.name,
                logoUrl: brand.logoUrl,
                isDefault: brand.isDefault,
              })),
            );
          } catch {
            // Keep the modal available so the connection can still be saved;
            // a later refresh will retry the target-list request.
            setGoogleAdsBrandOptions([]);
          }
        }
        setGoogleAdsAccountSelectionConnectionId(String(result.connectionId));
        return;
      }

      if (["google-analytics", "google-search-console"].includes(provider)) {
        try {
          const refreshedIntegration = await syncConnectedIntegrationMetadata(
            platformId,
            provider,
            isStoreOpsProvider(provider)
              ? resolveOnboardingStoreConnectionIdentity(
                  provider,
                  options?.connectionConfig || storeUrl,
                )
              : result?.connectionId,
          );
          const refreshedIntegrationId = resolveOnboardingWorkspaceIntegrationId(
            refreshedIntegration,
            result?.connectionId,
          );
          const hasResources =
            refreshedIntegrationId !== null &&
            (await prepareOnboardingResourceSelection(
              platformId,
              provider,
              refreshedIntegrationId,
              resolveOnboardingConnectionId(refreshedIntegration) || result?.connectionId,
            ));
          if (hasResources) {
            clearSyncingState();
            resetCredentialFormAfterSuccess();
            return;
          }
        } catch (error) {
          console.warn(
            "[Onboarding] Failed to prepare optional Google resource selection",
            error,
          );
        }
        clearSyncingState();
        resetCredentialFormAfterSuccess();
        return;
      }

      if (!result?.uiOpened) {
        const refreshedIntegration = await syncConnectedIntegrationMetadata(
          platformId,
          provider,
          isStoreOpsProvider(provider)
            ? resolveOnboardingStoreConnectionIdentity(
                provider,
                options?.connectionConfig || storeUrl,
              )
            : result?.connectionId,
        );
        clearSyncingState();
        resetCredentialFormAfterSuccess();
        const refreshedEligibilityPayload =
          buildEligibilityPayloadFromIntegration(refreshedIntegration);
        const refreshedEligibilityApplied =
          tracksTrialEligibility &&
          applyEligibilityFromConnection(
            platformId,
            refreshedEligibilityPayload as any,
          );
        if (
          refreshedEligibilityApplied &&
          shouldSuppressConnectionSuccessToastAfterEligibility(
            refreshedEligibilityPayload as any,
          )
        ) {
          return;
        }
        showToast(
          t("website.onboarding.connect.connectedSuccess", {
            platform: platformId,
          }),
          "success",
        );
        return;
      }
    } catch (error: any) {
      clearSyncingState();
      showConnectionFeedbackToast(error, platformId);
    }
  };

  const queueConnectionUntilWorkspaceReady = (
    platformId: string,
    provider: string,
  ) => {
    setPendingConnectionRequest({ platformId, provider });
    setSyncingTools((prev) => new Set(prev).add(platformId));
  };

  const handleConnect = (platformId: string, provider: string) => {
    if (syncingTools.has(platformId) || !canStartOnboardingConnection(provider)) return;
    setConnectionReview({ platformId, provider });
  };

  const continueReviewedConnection = async (platformId: string, provider: string) => {
    if (!workspaceReady) {
      queueConnectionUntilWorkspaceReady(platformId, provider);
      return;
    }

    await startConnectionFlow(platformId, provider);
  };

  const handleManualCredentialSubmit = async () => {
    if (
      !manualCredentialPlatformId ||
      manualCredentialConnectionInFlightRef.current
    ) {
      return;
    }

    const option = platformOptions.find(
      (platform) => platform.id === manualCredentialPlatformId,
    );
    if (!option) {
      return;
    }

    manualCredentialConnectionInFlightRef.current = true;
    try {
      await startConnectionFlow(manualCredentialPlatformId, option.provider, {
        connectionConfig: {
          apiKey: manualCredentialApiKey.trim(),
          storeUrl: manualCredentialStoreUrl.trim(),
          accountLabel: manualCredentialAccountLabel.trim() || undefined,
        },
      });
    } finally {
      manualCredentialConnectionInFlightRef.current = false;
    }
  };

  const handleIntegrationCredentialSubmit = async () => {
    const tool = credentialTool;
    if (
      !tool ||
      credentialConnectionInFlightRef.current ||
      !canSubmitIntegrationCredentialForm(tool, credentialValues)
    ) {
      return;
    }

    const option = platformOptions.find((platform) => platform.id === tool);
    if (!option) {
      return;
    }

    const { connectionConfig } = buildIntegrationCredentialConnection(
      tool,
      credentialValues,
    );
    credentialConnectionInFlightRef.current = true;
    try {
      await startConnectionFlow(tool, option.provider, { connectionConfig });
    } finally {
      credentialConnectionInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!pendingConnectionRequest) {
      return;
    }

    if (workspaceError) {
      setSyncingTools((prev) => {
        const next = new Set(prev);
        next.delete(pendingConnectionRequest.platformId);
        return next;
      });
      setPendingConnectionRequest(null);
      return;
    }

    if (!workspaceReady) {
      return;
    }

    const { platformId, provider } = pendingConnectionRequest;
    const connectEntryPoint = resolveIntegrationConnectEntryPoint(platformId, {
      hasLegacyStoreUrlInput: Boolean(STORE_URL_INPUT_CONFIG[platformId]),
      hasLegacyCredentialInput: isIntegrationCredentialTool(platformId),
    });
    if (connectEntryPoint.kind === "legacy-store-url") {
      setStoreConnectionLabel("");
      updateData({
        pendingConnectionRequest: null,
        storeConnectionPlatform: platformId,
        storeConnectionStoreUrl: "",
        storeConnectionStoreHash: "",
        storeConnectionAccountUuid: "",
      });
      setSyncingTools((prev) => {
        const next = new Set(prev);
        next.delete(platformId);
        return next;
      });
      return;
    }

    setPendingConnectionRequest(null);
    setSyncingTools((prev) => {
      const next = new Set(prev);
      next.delete(platformId);
      return next;
    });
    void startConnectionFlow(platformId, provider, {
      skipSyncingGuard: true,
    });
  }, [pendingConnectionRequest, workspaceError, workspaceReady]);

  const handleDisconnect = async (
    platformId: string,
    provider: string,
    options?: DisconnectIntegrationOptions,
  ) => {
    if (disconnectingPlatformId) {
      return false;
    }

    setDisconnectingPlatformId(platformId);
    try {
      await workspaceIntegrations.disconnect(provider, options);
      const integrations = await workspaceIntegrations.refresh();
      const connectedOptions = getConnectedOnboardingOptions(
        integrations,
        platformOptions,
      );
      const nextConnectedIntegrations = connectedOptions.map(
        (option) => option.id,
      );
      const nextTrialRequirements = removeConnectedIntegrationTrialRequirement(
        trialRequirementsRef.current,
        platformId,
      );
      const firstConnectedStore = connectedOptions.find(
        (option) => isStoreOpsProvider(option.provider),
      );
      const nextConnectedStoreProfile = firstConnectedStore
        ? resolveConnectedStoreProfile(
            integrations,
            firstConnectedStore.provider,
          )
        : { storeName: "", storeLogoUrl: null };
      connectedIntegrationsRef.current = nextConnectedIntegrations;
      trialRequirementsRef.current = nextTrialRequirements;
      updateData({
        connectedIntegrations: nextConnectedIntegrations,
        connectedStoreName: nextConnectedStoreProfile.storeName || "",
        connectedStoreLogoUrl: nextConnectedStoreProfile.storeLogoUrl || null,
        connectedIntegrationTrialRequirements: nextTrialRequirements,
        trialEligibilityState: resolveAggregatedOnboardingTrialEligibilityState(
          nextTrialRequirements,
        ),
      });
      showToast(
        t("website.onboarding.connect.disconnectedSuccess", {
          platform: platformId,
        }),
        "success",
      );
      return true;
    } catch (error: any) {
      showToast(
        error?.message ||
          t("website.onboarding.connect.disconnectFailed", {
            platform: platformId,
          }),
        "error",
      );
      return false;
    } finally {
      setDisconnectingPlatformId(null);
    }
  };

  const handlePluginConnectionDisconnect = async (
    instance: OnboardingConnectionInstance,
  ) => {
    if (disconnectingConnectionId) {
      return false;
    }

    const platform = platformOptions.find(
      (option) =>
        normalizeIntegrationProvider(option.provider) === instance.provider,
    );
    const platformId = platform?.id || instance.provider;
    setDisconnectingConnectionId(instance.id);

    try {
      await workspaceIntegrations.disconnect(
        instance.provider,
        buildOnboardingConnectionDisconnectOptions(instance),
      );
      const integrations = await workspaceIntegrations.refresh(false, {
        forceRefresh: true,
      });
      const nextInstances = resolveOnboardingConnectionInstances(integrations);
      const hasAnotherProviderConnection = nextInstances.some(
        (nextInstance) =>
          nextInstance.isValidConnection &&
          nextInstance.provider === instance.provider,
      );
      const connectedOptions = getConnectedOnboardingOptions(
        integrations,
        platformOptions,
      );
      const nextConnectedIntegrations = connectedOptions.map(
        (option) => option.id,
      );
      let nextTrialRequirements = removeConnectedIntegrationTrialRequirement(
        trialRequirementsRef.current,
        instance.id,
      );
      if (!hasAnotherProviderConnection) {
        nextTrialRequirements = removeConnectedIntegrationTrialRequirement(
          nextTrialRequirements,
          platformId,
        );
      }
      const firstConnectedStore = connectedOptions.find(
        (option) => isStoreOpsProvider(option.provider),
      );
      const nextConnectedStoreProfile = firstConnectedStore
        ? resolveConnectedStoreProfile(
            integrations,
            firstConnectedStore.provider,
          )
        : { storeName: "", storeLogoUrl: null };

      connectedIntegrationsRef.current = nextConnectedIntegrations;
      trialRequirementsRef.current = nextTrialRequirements;
      updateData({
        connectedIntegrations: nextConnectedIntegrations,
        connectedStoreName: nextConnectedStoreProfile.storeName || "",
        connectedStoreLogoUrl: nextConnectedStoreProfile.storeLogoUrl || null,
        connectedIntegrationTrialRequirements: nextTrialRequirements,
        trialEligibilityState: resolveAggregatedOnboardingTrialEligibilityState(
          nextTrialRequirements,
        ),
      });

      showToast(
        t("website.onboarding.connect.connectionDisconnectedSuccess", {
          connection:
            instance.label || instance.accountName || instance.externalIdentity,
        }),
        "success",
      );
      if (
        !nextInstances.some(
          (nextInstance) => nextInstance.provider === instance.provider,
        )
      ) {
        setExpandedPluginTool(null);
      }
      return true;
    } catch (error: any) {
      showToast(
        error?.message ||
          t("website.onboarding.connect.disconnectFailed", {
            platform: platformId,
          }),
        "error",
      );
      return false;
    } finally {
      setDisconnectingConnectionId(null);
    }
  };

  const confirmOnboardingDisconnect = async (
    confirmationKey: string,
    target: string,
    disconnect: () => Promise<boolean>,
  ) => {
    if (disconnectConfirmationKeysRef.current.has(confirmationKey)) {
      return false;
    }

    disconnectConfirmationKeysRef.current.add(confirmationKey);
    try {
      const confirmed = await showDialog({
        title: t("website.integrationsSettings.disconnectTitle", { target }),
        subtitle: t("website.integrationsSettings.disconnectSubtitle"),
        confirmText: t("website.integrationsSettings.disconnect"),
        cancelText: t("common.cancel"),
        destructive: true,
      });

      if (!confirmed) {
        return false;
      }

      return await disconnect();
    } finally {
      disconnectConfirmationKeysRef.current.delete(confirmationKey);
    }
  };

  const handlePluginConnectionLabelSave = async (
    instance: OnboardingConnectionInstance,
    overrideLabel?: string,
  ) => {
    const nextLabel = (
      overrideLabel ?? connectionLabelDrafts[instance.id] ?? instance.label
    ).trim();
    if (nextLabel === instance.label.trim()) {
      return;
    }
    if (
      savingLabelConnectionId ||
      !data.workspaceId ||
      !instance.workspaceIntegrationId
    ) {
      showToast(t("website.onboarding.connect.labelUpdateFailed"), "error");
      return;
    }

    setSavingLabelConnectionId(instance.id);
    try {
      const baseLabelUpdateRequestConfig = {
        headers: { "X-Workspace-Id": data.workspaceId },
      };
      const labelUpdateRequestConfig = isPluginOnboarding
        ? withPluginProductHeaders(baseLabelUpdateRequestConfig)
        : withProductHeaders("BUSTLY_CORE", baseLabelUpdateRequestConfig);
      await authService.updateWorkspaceIntegrationLabel(
        instance.workspaceIntegrationId,
        {
          label: nextLabel || null,
          axiosRequestConfig: labelUpdateRequestConfig,
        },
      );
      await workspaceIntegrations.refresh(false, { forceRefresh: true });
      showToast(t("website.onboarding.connect.labelUpdated"), "success");
    } catch (error: any) {
      showToast(
        error?.message || t("website.onboarding.connect.labelUpdateFailed"),
        "error",
      );
    } finally {
      setSavingLabelConnectionId(null);
    }
  };

  const persistOnboardingResourceSelection = async ({
    projectId,
    integrationId,
    provider,
    platformId = provider,
    resources,
    accessLevelsByResource,
  }: {
    projectId: string;
    integrationId: number;
    provider: string;
    platformId?: string;
    resources: IntegrationResource[];
    accessLevelsByResource?: Record<string, "READ" | "WRITE">;
  }) => {
    if (isPluginOnboarding) {
      return;
    }

    if (
      !data.workspaceId ||
      !Number.isSafeInteger(integrationId) ||
      integrationId <= 0
    ) {
      throw new Error("Missing numeric workspace integration id");
    }
    const { projectService } = await import("@common/api/projectService");
    // Parent assignment is structural; selected children carry the positive
    // ceiling. A DISABLED parent is still visible and can be raised later by
    // the application owner or a business-group owner with manage_access.
    await projectService.assignIntegration(
      data.workspaceId,
      projectId,
      integrationId,
      "DISABLED",
    );
    if (resources.length > 0) {
      await projectService.assignResources(
        data.workspaceId,
        projectId,
        resources.map((resource) => ({
          provider: resource.provider,
          resourceType: resource.resourceType,
          resourceKey: resource.resourceKey,
          // The resource contract may identify a freshly synced GA/GSC row by
          // the opaque Nango connection id. Assignment endpoints require the
          // numeric workspace integration id, so keep the selected resource
          // key/type and bind it back to the resolved parent integration.
          connectionId: integrationId,
          maxAccessLevel:
            accessLevelsByResource?.[
              normalizeOnboardingResourceKey(
                resource.resourceType,
                resource.resourceKey,
              )
            ] || ("WRITE" as const),
        })),
      );
    }
    const integrationProvider = getIntegrationProviderForTool(platformId) || provider;
    await syncConnectedIntegrationMetadata(platformId, integrationProvider);
  };

  const loadOnboardingDefaultResourceAssignmentSnapshot = async ({
    provider,
    integrationId,
    connectionId,
  }: {
    provider: string;
    integrationId: number;
    connectionId?: string | number | null;
  }): Promise<{
    defaultProjectId: string | null;
    resources: IntegrationResource[];
    validProjectIds: Set<string>;
  }> => {
    if (!data.workspaceId) {
      return { defaultProjectId: null, resources: [], validProjectIds: new Set() };
    }

    try {
      const { projectService } = await import("@common/api/projectService");
      const [contract, brands] = await Promise.all([
        projectService.getIntegrationContract(data.workspaceId),
        projectService.listBrands(data.workspaceId),
      ]);
      return {
        defaultProjectId: resolveDefaultBusinessGroupId(brands),
        validProjectIds: new Set(brands.map((brand) => brand.id)),
        resources: selectOnboardingSecondaryResources(
          contract.resources,
          provider,
          [integrationId, connectionId],
        ),
      };
    } catch (error) {
      console.warn(
        "[Onboarding] Failed to read default resource assignment snapshot",
        error,
      );
      return { defaultProjectId: null, resources: [], validProjectIds: new Set() };
    }
  };

  const handleGoogleAdsAccountSelectionSaved = async (
    payload: GoogleAdsAccountSelectionSavedPayload,
  ) => {
    const integrationId = Number(payload.integrationId);
    if (!Number.isSafeInteger(integrationId) || integrationId <= 0 || !data.workspaceId) {
      throw new Error("Google Ads integration id is not available yet");
    }
    if (isPluginOnboarding) {
      googleAdsAccountSelectionSavedRef.current = true;
      showToast(
        t("website.onboarding.connect.connectedSuccess", {
          platform: "Google Ads",
        }),
        "success",
      );
      return;
    }

    const selectedCustomerIds = new Set(
      (payload.selectedCustomerIds || []).map((id) =>
        normalizeOnboardingResourceKey("CUSTOMER", id),
      ),
    );
    googleAdsAccountSelectionSavedRef.current = true;
    showToast(
      t("website.onboarding.connect.connectedSuccess", {
        platform: "Google Ads",
      }),
      "success",
    );

    void (async () => {
      try {
        const {
          defaultProjectId,
          resources: availableResources,
          validProjectIds,
        } = await loadOnboardingDefaultResourceAssignmentSnapshot({
          provider: "google-ads",
          integrationId,
          connectionId: payload.connectionId,
        });
        if (!defaultProjectId) {
          return;
        }
        const resourcesByCustomerId = new Map<string, IntegrationResource[]>();
        availableResources
          .filter((resource) =>
            selectedCustomerIds.has(
              normalizeOnboardingResourceKey(
                resource.resourceType,
                resource.resourceKey,
              ),
            ),
          )
          .forEach((resource) => {
            const customerId = normalizeOnboardingResourceKey(
              "CUSTOMER",
              resource.resourceKey,
            );
            const customerResources = resourcesByCustomerId.get(customerId) || [];
            customerResources.push(resource);
            resourcesByCustomerId.set(customerId, customerResources);
          });

        const resourcesByProjectId = new Map<
          string,
          { resources: IntegrationResource[]; accessLevelsByResource: Record<string, "READ" | "WRITE"> }
        >();
        for (const customerId of selectedCustomerIds) {
          const customerResources = resourcesByCustomerId.get(customerId) || [];
          const selectedProjectIds = (payload.selectedCustomerBrandIds?.[customerId]?.length
            ? payload.selectedCustomerBrandIds[customerId]
            : [defaultProjectId])
            .filter((projectId) => validProjectIds.has(projectId));
          const selectedAccessLevels = payload.selectedCustomerBrandAccessLevels?.[customerId] || {};
          for (const projectId of selectedProjectIds) {
            const projectSelection = resourcesByProjectId.get(projectId) || {
              resources: [],
              accessLevelsByResource: {},
            };
            customerResources.forEach((resource) => {
              const resourceKey = normalizeOnboardingResourceKey(
                resource.resourceType,
                resource.resourceKey,
              );
              if (!projectSelection.resources.some((item) =>
                normalizeOnboardingResourceKey(item.resourceType, item.resourceKey) === resourceKey,
              )) {
                projectSelection.resources.push(resource);
              }
              projectSelection.accessLevelsByResource[resourceKey] =
                selectedAccessLevels[projectId] || "READ";
            });
            resourcesByProjectId.set(projectId, projectSelection);
          }
        }

        if (resourcesByProjectId.size === 0) {
          await persistOnboardingResourceSelection({
            projectId: defaultProjectId,
            integrationId,
            provider: "google-ads",
            platformId: "Google Ads",
            resources: [],
          });
          return;
        }

        for (const [projectId, projectSelection] of resourcesByProjectId) {
          await persistOnboardingResourceSelection({
            projectId,
            integrationId,
            provider: "google-ads",
            platformId: "Google Ads",
            resources: projectSelection.resources,
            accessLevelsByResource: projectSelection.accessLevelsByResource,
          });
        }
      } catch (error) {
        console.warn(
          "[Onboarding] Failed to persist Google Ads onboarding selection after the selection save completed",
          error,
        );
      }
    })();
  };

  const prepareOnboardingResourceSelection = async (
    platformId: string,
    provider: string,
    integrationId: number,
    nangoConnectionId?: string | null,
  ): Promise<boolean> => {
    if (
      !data.workspaceId ||
      !["google-analytics", "google-search-console"].includes(provider) ||
      !Number.isSafeInteger(integrationId) ||
      integrationId <= 0 ||
      !nangoConnectionId
    ) {
      return false;
    }
    const requestId = ++onboardingResourceSelectionRequestIdRef.current;
    try {
      const { projectService } = await import("@common/api/projectService");
      const { brands, resources } = await loadOnboardingResourceSelectionData({
        loadContract: () => projectService.getIntegrationContract(data.workspaceId!),
        // Onboarding persists newly selected Google child resources into the
        // default business group. Use the workspace project list, not the
        // assignment-scoped project list, because the latter can legitimately
        // hide the default project for non-owner users.
        loadBrands: () => projectService.listBrands(data.workspaceId!),
        provider,
        integrationId,
        connectionId: nangoConnectionId,
        requireDefaultBusinessGroup: false,
        refreshResources: () => authService.refreshNangoGoogleResources({
          provider: provider as "google-analytics" | "google-search-console",
          connectionId: nangoConnectionId,
          axiosRequestConfig: { headers: { "X-Workspace-Id": data.workspaceId } },
        }),
      });
      if (onboardingResourceSelectionRequestIdRef.current !== requestId) {
        return false;
      }
      if (resources.length === 0) {
        return false;
      }
      const projectId = resolveDefaultBusinessGroupId(brands);
      setOnboardingResourceSelection({
        displayProvider: platformId,
        provider,
        workspaceIntegrationId: integrationId,
        nangoConnectionId,
        resources,
        projectId,
        isLoading: false,
      });
      return true;
    } catch (error) {
      if (onboardingResourceSelectionRequestIdRef.current === requestId) {
        console.warn(
          "[Onboarding] Failed to load onboarding resource selection",
          error,
        );
      }
      return false;
    }
  };

  const handleOnboardingResourceSelectionSaved = async (
    resources: IntegrationResource[],
  ) => {
    if (!onboardingResourceSelection || !data.workspaceId) return;
    setIsSavingOnboardingResources(true);
    try {
      if (
        !Number.isSafeInteger(onboardingResourceSelection.workspaceIntegrationId) ||
        onboardingResourceSelection.workspaceIntegrationId <= 0
      ) {
        throw new Error("Onboarding integration id is not numeric");
      }
      if (onboardingResourceSelection.isLoading) {
        throw new Error("Onboarding resource selection is still loading");
      }
      const saveRequest = {
        provider: onboardingResourceSelection.provider as
          | "google-analytics"
          | "google-search-console",
        connectionId: onboardingResourceSelection.nangoConnectionId,
        selectedResourceKeys: resources.map((resource) =>
          normalizeOnboardingResourceKey(
            resource.resourceType,
            resource.resourceKey,
          ),
        ),
        axiosRequestConfig: { headers: { "X-Workspace-Id": data.workspaceId } },
      };
      await authService.saveNangoGoogleResourceSelection(saveRequest);
      const selection = onboardingResourceSelection;
      onboardingResourceSelectionRequestIdRef.current += 1;
      setOnboardingResourceSelection(null);
      showToast(
        t("website.onboarding.connect.connectedSuccess", {
          platform: selection.displayProvider,
        }),
        "success",
      );

      void (async () => {
        try {
          if (!isPluginOnboarding && selection.projectId) {
            await persistOnboardingResourceSelection({
              projectId: selection.projectId,
              integrationId: selection.workspaceIntegrationId,
              provider: selection.provider,
              platformId: selection.displayProvider,
              resources,
            });
          }
        } catch (error) {
          console.warn(
            "[Onboarding] Failed to persist onboarding resource selection after the selection save completed",
            error,
          );
        }
      })();
    } catch (error) {
      showConnectionFeedbackToast(error, onboardingResourceSelection.provider);
    } finally {
      setIsSavingOnboardingResources(false);
    }
  };

  const handleOnboardingResourceSelectionCancel = () => {
    onboardingResourceSelectionRequestIdRef.current += 1;
    setOnboardingResourceSelection(null);
  };

  const handleGoogleAdsAccountSelectionClose = async () => {
    if (googleAdsAccountSelectionSavedRef.current) {
      googleAdsAccountSelectionSavedRef.current = false;
      setGoogleAdsAccountSelectionConnectionId(null);
      setGoogleAdsAccountSelectionDiscovery(null);
      setGoogleAdsBrandOptions([]);
      return;
    }

    const didDisconnect = await handleDisconnect("Google Ads", "google-ads", {
      connectionId: googleAdsAccountSelectionConnectionId || undefined,
      connectionScene: "onboarding",
    });
    if (didDisconnect) {
      setGoogleAdsAccountSelectionConnectionId(null);
      setGoogleAdsAccountSelectionDiscovery(null);
      setGoogleAdsBrandOptions([]);
    }
  };

  const handleConnectionSubmit = async () => {
    if (!connectingTool) {
      return;
    }

    if (urlConnectionInFlightRef.current) {
      return;
    }

    const option = platformOptions.find(
      (platform) => platform.id === connectingTool,
    );
    if (!option) {
      return;
    }
    if (!canStartOnboardingConnection(option.provider)) {
      resetConnectionForm();
      return;
    }

    const activeConnectingTool = connectingTool;
    const submittedStoreUrl = storeUrl.trim();
    const submittedConnectionLabel = storeConnectionLabel.trim();
    const submittedShopifyStoreDomain =
      normalizeShopifyStoreDomainInput(storeUrl);
    const onboardingConnectionLabelConfig = buildOnboardingConnectionLabelConfig({
      label: submittedConnectionLabel,
    });
    let keepSyncingUntilCallback = false;
    const handleStoreConnectionFailureResult = (
      payload?: (ConnectRestrictionPayload & { success?: boolean }) | null,
    ) => {
      const restriction = normalizeConnectRestriction(payload);
      if (payload?.success !== false && !restriction.blockConnect) {
        return false;
      }

      showConnectionFeedbackToast(restriction.message, activeConnectingTool);
      resetConnectionForm();
      return true;
    };

    const submittedStoreIdentity = resolveOnboardingStoreConnectionIdentity(
      option.provider,
      option.provider === "shopify"
        ? { shop_domain: submittedShopifyStoreDomain }
        : option.provider === "bigcommerce"
          ? { store_hash: bigCommerceStoreHash.trim() }
          : submittedStoreUrl,
    );
    const hasDuplicateStoreConnection =
      isStoreOpsProvider(option.provider) &&
      hasDuplicateOnboardingStoreConnection(
        pluginConnectionInstances,
        option.provider,
        submittedStoreIdentity,
      );
    if (hasDuplicateStoreConnection) {
      showToast(
        t("website.onboarding.connect.duplicateStoreConnection", {
          store: submittedStoreIdentity,
        }),
        "info",
      );
      resetConnectionForm();
      return;
    }

    try {
      urlConnectionInFlightRef.current = true;
      setSyncingTools((prev) => new Set(prev).add(activeConnectingTool));

      const commonConnectOptions = {
        onAuthorizationStarted: () => {
          resetConnectionForm();
        },
        onSuccess: async () => {
          const refreshedIntegration = await syncConnectedIntegrationMetadata(
            activeConnectingTool,
            option.provider,
            submittedStoreIdentity,
          );
          const refreshedEligibilityPayload =
            buildEligibilityPayloadFromIntegration(refreshedIntegration);
          const eligibilityPayload = isStoreOpsProvider(option.provider)
            ? refreshedEligibilityPayload
            : undefined;

          setSyncingTools((prev) => {
            const next = new Set(prev);
            next.delete(activeConnectingTool);
            return next;
          });

          const eligibilityApplied = applyEligibilityFromConnection(
            activeConnectingTool,
            eligibilityPayload as any,
            submittedStoreUrl,
          );
          if (
            eligibilityApplied &&
            shouldSuppressConnectionSuccessToastAfterEligibility(
              eligibilityPayload as any,
            )
          ) {
            return;
          }
          showToast(
            t("website.onboarding.connect.connectedSuccess", {
              platform: activeConnectingTool,
            }),
            "success",
          );
        },
        onError: (message: string) => {
          setSyncingTools((prev) => {
            const next = new Set(prev);
            next.delete(activeConnectingTool);
            return next;
          });
          showConnectionFeedbackToast(message, activeConnectingTool);
        },
        onRestriction: (restriction: any) => {
          setSyncingTools((prev) => {
            const next = new Set(prev);
            next.delete(activeConnectingTool);
            return next;
          });
          applyEligibilityFromConnection(
            activeConnectingTool,
            restriction,
            submittedStoreUrl,
          );
        },
      };

      let result;
      if (option.provider === "shopify") {
        result = await workspaceIntegrations.connect(
          option.provider,
          {
            shop_domain: submittedShopifyStoreDomain,
            redirect_url: buildShopifyOnboardingConnectRedirectUrl(
              window.location.href,
              data.workspaceId,
            ),
            connection_scene: "onboarding",
            ...onboardingConnectionLabelConfig,
          },
          commonConnectOptions,
        );
      } else if (option.provider === "bigcommerce") {
        result = await workspaceIntegrations.connect(
          option.provider,
          {
            store_domain: `https://store-${submittedStoreUrl}.mybigcommerce.com/`,
            store_hash: bigCommerceStoreHash.trim(),
            accountUuid: bigCommerceAccountUuid.trim(),
            connection_scene: "onboarding",
            ...onboardingConnectionLabelConfig,
          },
          commonConnectOptions,
        );
      } else {
        throw new Error(
          `Unsupported onboarding connection provider: ${option.provider}`,
        );
      }

      if (
        result?.success === false &&
        applyEligibilityFromConnection(
          connectingTool,
          result,
          submittedStoreUrl,
        )
      ) {
        resetConnectionForm();
        return;
      }

      if (handleStoreConnectionFailureResult(result)) {
        return;
      }

      if (result?.action === "REDIRECT" && result?.url) {
        window.location.href = result.url;
        return;
      }

      if (result?.uiOpened) {
        keepSyncingUntilCallback = true;
        resetConnectionForm();
        return;
      }

      const refreshedIntegration = await syncConnectedIntegrationMetadata(
        activeConnectingTool,
        option.provider,
        submittedStoreIdentity,
      );
      const refreshedEligibilityPayload =
        buildEligibilityPayloadFromIntegration(refreshedIntegration);
      const eligibilityPayload = isStoreOpsProvider(option.provider)
        ? mergeEligibilityPayload(refreshedEligibilityPayload, result)
        : result;

      const eligibilityApplied = applyEligibilityFromConnection(
        activeConnectingTool,
        eligibilityPayload,
        submittedStoreUrl,
      );
      if (
        eligibilityApplied &&
        shouldSuppressConnectionSuccessToastAfterEligibility(eligibilityPayload)
      ) {
        resetConnectionForm();
        return;
      }
      showToast(
        t("website.onboarding.connect.connectedSuccess", {
          platform: activeConnectingTool,
        }),
        "success",
      );
      resetConnectionForm();
    } catch (error: any) {
      showConnectionFeedbackToast(error, activeConnectingTool);
    } finally {
      if (keepSyncingUntilCallback) {
        urlConnectionInFlightRef.current = false;
        return;
      }
      urlConnectionInFlightRef.current = false;
      setSyncingTools((prev) => {
        const next = new Set(prev);
        next.delete(activeConnectingTool);
        return next;
      });
    }
  };

  const knownConnectedFromData = data.connectedIntegrations || [];
  const hasFreshConnectionSnapshot =
    workspaceIntegrations.hasFetchedCurrentWorkspace &&
    !workspaceIntegrations.error;
  const restoredConnectionUsage = useMemo(
    () =>
      resolvePluginConnectionUsage(
        resolveOnboardingConnectionInstances(
          workspaceIntegrations.allIntegrations ?? [],
        ),
      ),
    [workspaceIntegrations.allIntegrations],
  );
  useEffect(() => {
    if (!hasFreshConnectionSnapshot || !data.workspaceId) {
      return;
    }
    if (
      data.connectedStoreCount ===
      restoredConnectionUsage.storeConnections
    ) {
      return;
    }
    updateData({
      connectedStoreCount: restoredConnectionUsage.storeConnections,
    });
  }, [
    data.connectedStoreCount,
    data.workspaceId,
    hasFreshConnectionSnapshot,
    restoredConnectionUsage.storeConnections,
    updateData,
  ]);
  const connectionRequirementsState = useMemo(
    () =>
      resolveOnboardingConnectionRequirements({
        serverIntegrations: workspaceIntegrations.allIntegrations,
        persistedConnectedToolNames: knownConnectedFromData,
        serverHydrated: Boolean(data.workspaceId) && hasFreshConnectionSnapshot,
        requiredConnectionCount,
        connectionRequirementBasis,
        requiresStoreConnection: false,
        excludedCategoryIds: isPluginOnboarding
          ? PLUGIN_ONBOARDING_EXCLUDED_CATEGORY_IDS
          : [],
        connectionCategories: getOnboardingConnectionCategories(
          isPluginOnboarding,
        ),
      }),
    [
      data.workspaceId,
      hasFreshConnectionSnapshot,
      knownConnectedFromData,
      connectionRequirementBasis,
      isPluginOnboarding,
      requiredConnectionCount,
      workspaceIntegrations.allIntegrations,
    ],
  );
  useEffect(() => {
    if (!hasFreshConnectionSnapshot || syncingTools.size === 0) {
      return;
    }

    const connectedToolSet = new Set(connectionRequirementsState.connectedTools);
    setSyncingTools((currentSyncingTools) => {
      let changed = false;
      const nextSyncingTools = new Set(currentSyncingTools);

      currentSyncingTools.forEach((tool) => {
        if (connectedToolSet.has(tool)) {
          nextSyncingTools.delete(tool);
          changed = true;
        }
      });

      return changed ? nextSyncingTools : currentSyncingTools;
    });
  }, [
    connectionRequirementsState.connectedTools,
    hasFreshConnectionSnapshot,
    syncingTools.size,
  ]);
  const hasConnectionOperationInFlight =
    hasShopifyReturn || shopifyReturnState === "checking" || shopifyEntryState === "checking" ||
    syncingTools.size > 0 ||
    Boolean(disconnectingPlatformId) ||
    Boolean(disconnectingConnectionId) ||
    Boolean(savingLabelConnectionId);
  const canAdvance = canContinueOnboardingConnectionStep({
    requirements: connectionRequirementsState,
    hasFreshServerSnapshot: hasFreshConnectionSnapshot,
    hasPendingAccountSelection: Boolean(googleAdsAccountSelectionConnectionId),
    hasConnectionOperationInFlight,
  });
  const isConnectionStepLoading = resolveOnboardingConnectionStepLoading({
    isPluginOnboarding,
    hasFreshServerSnapshot: hasFreshConnectionSnapshot,
    isStepSubmitting: isLoading,
    isRefreshingConnections: workspaceIntegrations.isLoading,
  });
  const isManualCredentialConnecting = manualCredentialPlatformId
    ? syncingTools.has(manualCredentialPlatformId)
    : false;
  const isIntegrationCredentialConnecting = credentialTool
    ? syncingTools.has(credentialTool)
    : false;

  const renderPluginConnectionDetails = (
    platform: OnboardingIntegrationOption,
    instances: readonly OnboardingConnectionInstance[],
    additionState: PluginConnectionAdditionState,
  ) => {
    const limitMessage = additionState.allowed
      ? undefined
      : getPluginConnectionLimitMessage(additionState);
    const isAddingConnection = syncingTools.has(platform.id);
    const hasProviderTrialUsed = instances.some((instance) =>
      isOnboardingTrialUnavailableConnection(instance),
    );

    return (
      <div
        data-onboarding-connection-details-content={platform.id}
        className="space-y-3"
      >
        <div className="space-y-2">
          <div className="hidden grid-cols-[minmax(0,1.8fr)_120px_140px_36px] gap-4 px-4 pb-1 text-[12px] font-medium text-[#676274] whitespace-nowrap sm:grid">
            <span>
              {t(
                isStoreOpsProvider(platform.provider)
                  ? "website.integrationsSettings.storeUrl"
                  : "website.integrationsSettings.account",
              )}
            </span>
            <span>{t("website.integrationsSettings.label")}</span>
            <span>{t("website.integrationsSettings.connectionStatus")}</span>
            <span className="sr-only">
              {t("website.integrationsSettings.action")}
            </span>
          </div>

          {instances.map((instance) => {
            const statusPresentation =
              getConnectionStatusPresentation(instance);
            const isSavingLabel = savingLabelConnectionId === instance.id;
            const isDisconnecting = disconnectingConnectionId === instance.id;
            const isInstanceTrialUsed =
              hasProviderTrialUsed ||
              isOnboardingTrialUnavailableConnection(instance);

            return (
              <div
                key={instance.id}
                className="grid gap-4 rounded-[14px] border border-[#E6E8F0] bg-[#FBFBFD] px-4 py-3 sm:grid-cols-[minmax(0,1.8fr)_120px_140px_36px] sm:items-center"
              >
                <div className="min-w-0">
                  <div className="mb-1 text-[11px] font-medium uppercase tracking-[0.04em] text-[#8A93B2] sm:hidden">
                    {t(
                      isStoreOpsProvider(platform.provider)
                        ? "website.integrationsSettings.storeUrl"
                        : "website.integrationsSettings.account",
                    )}
                  </div>
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="truncate text-[13.5px] font-semibold text-[#1A162F]"
                      title={instance.accountName}
                    >
                      {instance.accountName || instance.externalIdentity}
                    </span>
                    {isInstanceTrialUsed ? (
                      <span
                        className="shrink-0 rounded-md bg-[#FFF1B8] px-2 py-0.5 text-[11px] font-semibold leading-5 text-[#B26A00]"
                        title={
                          instance.eligibilityMessage ||
                          instance.eligibilityMessageFallback ||
                          t("website.onboarding.notice.freeTrialUsedTooltip")
                        }
                      >
                        {t("website.onboarding.notice.freeTrialUsedTag")}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    className="truncate text-[13px] font-medium text-[#1A162F]"
                    title={instance.label || undefined}
                  >
                    {instance.label || "-"}
                  </span>
                  <Tooltip
                    content={t("website.integrationsSettings.editLabelAction")}
                    side="top"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setEditingLabelInstance(instance);
                        setEditingLabelValue(
                          connectionLabelDrafts[instance.id] ?? instance.label ?? "",
                        );
                      }}
                      disabled={isSavingLabel}
                      aria-label={t("website.integrationsSettings.editLabelAction")}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md p-1 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] focus-visible:bg-[#F4F5F8] focus-visible:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSavingLabel ? (
                        <CircleNotch
                          size={13}
                          weight="bold"
                          className="animate-spin text-[#666F8D]"
                        />
                      ) : (
                        <PencilSimple size={13} weight="bold" />
                      )}
                    </button>
                  </Tooltip>
                </div>

                <div className="min-w-0">
                  <div className="mb-1 text-[11px] font-medium uppercase tracking-[0.04em] text-[#8A93B2] sm:hidden">
                    {t("website.integrationsSettings.connectionStatus")}
                  </div>
                  <div
                    className={clsx(
                      "flex items-center gap-1.5 text-[12.5px] font-medium whitespace-nowrap",
                      statusPresentation.textClassName,
                    )}
                  >
                    <span
                      className={clsx(
                        "h-1.5 w-1.5 shrink-0 rounded-full",
                        statusPresentation.dotClassName,
                      )}
                    />
                    <span>{t(statusPresentation.key)}</span>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Tooltip content={t("website.onboarding.connect.disconnect")} side="top">
                    <button
                      type="button"
                      onClick={() => {
                        void confirmOnboardingDisconnect(
                          `connection:${instance.id}`,
                          instance.accountName ||
                            instance.externalIdentity ||
                            instance.label ||
                            instance.provider,
                          () => handlePluginConnectionDisconnect(instance),
                        );
                      }}
                      disabled={
                        isDisconnecting ||
                        isSavingLabel ||
                        Boolean(disconnectingConnectionId)
                      }
                      aria-label={`${t("website.onboarding.connect.disconnect")} ${instance.accountName || instance.externalIdentity}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-[#8A93B2] transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isDisconnecting ? (
                        <CircleNotch size={14} weight="bold" className="animate-spin" />
                      ) : (
                        <LinkBreak size={16} weight="bold" />
                      )}
                    </button>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-88px)] w-full max-w-[1140px] flex-col items-center justify-center px-6 py-12 lg:px-8">
      <div className="flex w-full flex-col items-center">
        <div className="flex w-full flex-col items-center text-left">
          <div className="mb-8 flex w-full max-w-[1040px] flex-col items-center text-center">
            <h2 className="max-w-full text-[32px] font-medium leading-[1.1] text-[#1A162F] [text-wrap:balance] sm:text-[36px] lg:text-[40px]">
              {t(
                isPluginOnboarding
                  ? "website.onboarding.connect.pluginTitle"
                  : "website.onboarding.connect.title",
              )}
            </h2>
              <div className="mt-6 flex w-full justify-center">
                <ConnectionTrustTags
                  isPlugin={isPluginOnboarding}
                  showPrivacyLink={false}
                  badges={[
                    {
                      id: "infrastructure",
                      label: t("website.onboarding.connect.trustBadge2Tag"),
                      description: t("website.onboarding.connect.trustBadge2Desc"),
                      icon: <CloudCheck size={15} weight="regular" />,
                    },
                    {
                      id: "platform-partner",
                      label: t("website.onboarding.connect.trustBadge4Tag"),
                      description: t(
                        isPluginOnboarding
                          ? "website.onboarding.connect.pluginTrustBadge4Desc"
                          : "website.onboarding.connect.trustBadge4Desc",
                      ),
                      icon: <SealCheck size={15} weight="regular" />,
                    },
                    {
                      id: "local-task-information",
                      label: t(isPluginOnboarding
                        ? "website.onboarding.connect.pluginTrustBadge3Tag"
                        : "website.onboarding.connect.trustBadge3Tag"),
                      description: t(
                        isPluginOnboarding
                          ? "website.onboarding.connect.pluginTrustBadge3Desc"
                          : "website.onboarding.connect.trustBadge3Desc",
                      ),
                      icon: <HardDrive size={15} weight="regular" />,
                    },
                  ]}
                />
              </div>
          </div>

          <div className="flex w-full max-w-[904px] flex-col items-center">
            {data.trialEligibilityState === "checkout_required" ? (
              <div
                role="status"
                className="mb-5 flex w-full items-start justify-between gap-3 rounded-2xl border border-amber-200/80 bg-amber-50/80 px-5 py-4 text-left text-amber-900 shadow-sm animate-in fade-in"
              >
                <div className="flex items-start gap-3">
                  <WarningCircle
                    size={20}
                    weight="fill"
                    className="mt-0.5 shrink-0 text-amber-600"
                  />
                  <div>
                    <div className="text-sm font-semibold">
                      {t("website.onboarding.notice.freeTrialUnavailableBannerTitle")}
                    </div>
                    <div className="mt-0.5 text-xs leading-relaxed text-amber-800/90">
                      {t("website.onboarding.notice.freeTrialUnavailableBannerDescription")}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}


            {workspaceIntegrations.error ? (
              <div
                role="alert"
                className="mb-5 flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700"
              >
                <span>{t("website.onboarding.connect.statusLoadFailed")}</span>
                <button
                  type="button"
                  onClick={() => {
                    void workspaceIntegrations.refresh();
                  }}
                  className="rounded-full border border-red-200 bg-white px-4 py-2 text-[13px] font-bold text-red-700 transition-colors hover:bg-red-100"
                >
                  {t("common.retry")}
                </button>
              </div>
            ) : null}

            {workspaceError ? (
              <div
                role="alert"
                className="mb-5 w-full rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700"
              >
                {workspaceError}
              </div>
            ) : null}

            <div className="flex w-full flex-col gap-5 text-left md:flex-row md:items-start">
              <aside
                aria-label={t("website.integrationsSettings.integrations")}
                className="w-full shrink-0 md:w-[224px] md:border-r md:border-[#E4E7EF] md:pr-4"
              >
                <nav
                  aria-label={t("website.integrationsSettings.integrations")}
                  className="flex gap-1 overflow-x-auto pb-1 md:flex-col md:overflow-visible"
                >
                  {browseCategories.map((category) => {
                    const isActive = selectedCategory === category.id;
                    const categoryLabel = t(
                      ONBOARDING_CATEGORY_LABEL_KEYS[category.id],
                    );
                    const categoryTools =
                      connectionRequirementsState.categories[category.id].tools;
                    const categoryToolCount = categoryTools.length;
                    const isCategoryStoreLimitReached =
                      category.id === "store_ops" &&
                      categoryTools.every((tool) => isStoreOpsProvider(getIntegrationProviderForTool(tool))) &&
                      onboardingConnectionUsage.remainingStoreConnections === 0;

                    const categoryBtn = (
                      <button
                        key={category.id}
                        type="button"
                        aria-current={isActive ? "page" : undefined}
                        onClick={() => setSelectedCategory(category.id)}
                        className={clsx(
                          "flex min-h-10 shrink-0 items-center gap-2 rounded-[10px] px-3 py-2 text-left text-sm font-normal transition-colors md:w-full",
                          isActive
                            ? "bg-[#EEF1F6] text-[#1A162F]"
                            : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]",
                        )}
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
                          <span className="truncate">{categoryLabel}</span>
                          {isCategoryStoreLimitReached ? (
                            <span
                              aria-label={t(
                                "website.onboarding.connect.storeConnectionUsage",
                                {
                                  used: onboardingConnectionUsage.storeConnections,
                                  limit: onboardingConnectionLimits.storeConnections,
                                },
                              )}
                              className={clsx(
                                "inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium tracking-tight",
                                isActive
                                  ? "bg-white text-[#3E4559] shadow-xs"
                                  : "bg-[#EEF1F6] text-[#555E77]",
                              )}
                            >
                              {onboardingConnectionUsage.storeConnections}/
                              {onboardingConnectionLimits.storeConnections}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={clsx(
                            "text-xs font-normal tabular-nums",
                            isActive ? "text-[#666F8D]" : "text-[#8A93B2]",
                          )}
                        >
                          {categoryToolCount}
                        </span>
                      </button>
                    );

                    return categoryBtn;
                  })}
                </nav>
              </aside>

              <div
                id="integrations-list"
                className="min-w-0 flex-1 flex flex-col gap-5 min-h-[520px] max-w-[660px]"
              >
                {browseCategories.map((category) => {
                  const categoryTools =
                    connectionRequirementsState.categories[category.id].tools;
                  const categoryConnectedTools = categoryTools.filter((tool) =>
                    connectionRequirementsState.connectedTools.includes(tool),
                  );
                  const categoryLabel = t(
                    ONBOARDING_CATEGORY_LABEL_KEYS[category.id],
                  );
                  const categoryDescription = t(
                    ONBOARDING_CATEGORY_DESCRIPTION_KEYS[category.id],
                  );

                  return (
                    <section
                      key={category.id}
                      data-onboarding-integration-category={category.id}
                      className={clsx(
                        "w-full",
                        selectedCategory === category.id
                          ? "block animate-in fade-in duration-200"
                          : "hidden",
                      )}
                    >
                      <StepConnectIntegrationLayout>
                        {categoryTools.map((tool) => {
                          const platform = platformOptions.find(
                            (option) => option.id === tool,
                          );
                          if (!platform) {
                            return null;
                          }

                          const normalizedProvider =
                            normalizeIntegrationProvider(platform.provider);
                          const instances = pluginConnectionInstances.filter(
                            (instance) => instance.provider === normalizedProvider,
                          );
                          const additionState =
                            resolveOnboardingConnectionAdditionState({
                              instances: pluginConnectionInstances,
                              provider: platform.provider,
                              isPluginOnboarding,
                            });

                          const hasInstances = instances.length > 0;
                          const isExpanded = hasInstances
                            ? !collapsedPluginTools.has(tool)
                            : expandedPluginTool === tool ||
                              manuallyExpandedTools.has(tool);

                          return (
                            <PluginStepConnectIntegrationCard
                              key={tool}
                              tool={tool}
                              instances={instances}
                              isRestoringConnection={
                                !hasFreshConnectionSnapshot &&
                                categoryConnectedTools.includes(tool)
                              }
                              isExpanded={isExpanded}
                              isSyncing={syncingTools.has(tool) || (tool === "Shopify" && (hasShopifyReturn || shopifyReturnState === "checking" || shopifyEntryState === "checking"))}
                              hasTrialUsed={
                                instances.some((instance) =>
                                  isOnboardingTrialUnavailableConnection(instance),
                                ) ||
                                trialRequirementsRef.current?.[tool] === "checkout_required" ||
                                trialRequirementsRef.current?.[platform.id] === "checkout_required"
                              }
                              additionState={additionState}
                              limitMessage={
                                additionState.allowed
                                  ? undefined
                                  : getPluginConnectionLimitMessage(additionState)
                              }
                              onConnect={() => {
                                void handleConnect(platform.id, platform.provider);
                              }}
                              onToggle={() => {
                                if (hasInstances) {
                                  setCollapsedPluginTools((current) => {
                                    const next = new Set(current);
                                    if (next.has(tool)) next.delete(tool);
                                    else next.add(tool);
                                    return next;
                                  });
                                } else {
                                  setManuallyExpandedTools((current) => {
                                    const next = new Set(current);
                                    if (next.has(tool)) next.delete(tool);
                                    else next.add(tool);
                                    return next;
                                  });
                                  setExpandedPluginTool((current) =>
                                    current === tool ? null : tool,
                                  );
                                }
                              }}
                              details={
                                hasInstances
                                  ? renderPluginConnectionDetails(
                                      platform,
                                      instances,
                                      additionState,
                                    )
                                  : undefined
                              }
                              connectionProgress={tool === "Shopify" ? <>
                            <OnboardingShopifyInstall
                              workspaceId={data.workspaceId || ""}
                              onProgress={setShopifyReturnState}
                              onRestart={() => {
                                setShopifyReturnState("idle");
                                updateData({storeConnectionPlatform: "Shopify"});
                              }}
                              onConnected={() => workspaceIntegrations.refresh(false, {forceRefresh: true})}
                            />
                            {shopifyEntryState === "checking" && <p role="status" className="px-5 pb-4 text-sm text-[#666F8D]">{t("website.integrationsSettings.connecting")}</p>}
                            {shopifyEntryState === "error" && (
                              <div role="alert" className="px-5 py-3 text-sm text-[#666F8D]">
                                {t("website.shopifyConnectionEntry.error")}
                                <button type="button" className="ml-3 underline" onClick={() => {
                                  setShopifyEntryState("checking");
                                  setShopifyEntryAttempt(value => value + 1);
                                }}>{t("website.shopifyConnectionEntry.retry")}</button>
                                <button type="button" className="ml-3 underline" onClick={() => {
                                  resetConnectionForm();
                                  setShopifyEntryState("idle");
                                }}>{t("common.cancel")}</button>
                              </div>
                            )}
                            </> : undefined}
                            />
                          );
                        })}
                      </StepConnectIntegrationLayout>
                    </section>
                  );
                })}
              </div>
            </div>

            <div className="sticky bottom-0 z-20 mt-6 flex w-full flex-wrap items-center justify-between gap-3 border-t border-[#E6E8F0]/80 bg-[#F8F9FB]/90 py-4 backdrop-blur-md transition-all duration-300">
              <div className="flex items-center gap-3">
                <button
                  onClick={onBack}
                  className="flex min-w-[100px] items-center justify-center rounded-full border border-gray-200 bg-white px-6 py-3 text-[14px] font-bold text-[#1A162F] shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow"
                >
                  {t("website.onboarding.common.back")}
                </button>
              </div>
              <div className="flex items-center gap-4">
                <span
                  role="status"
                  aria-live="polite"
                  className="text-[14px] font-medium text-[#666F8D]"
                >
                  {t("website.onboarding.connect.connectionProgress", {
                    connected: connectionRequirementsState.connectedCount,
                    plural:
                      connectionRequirementsState.connectedCount === 1 ? "" : "s",
                    required:
                      connectionRequirementsState.requiredConnectionCount,
                  })}
                </span>
                <button
                  onClick={() => {
                    void onNext();
                  }}
                  disabled={!canAdvance || isConnectionStepLoading}
                  className={clsx(
                    "flex items-center gap-2 rounded-full px-8 py-3 text-[15px] font-bold transition-all shadow-lg",
                    canAdvance && !isConnectionStepLoading
                      ? "bg-[#1A162F] text-white hover:-translate-y-0.5 hover:bg-[#1A162F]/90 hover:shadow-xl"
                      : "cursor-not-allowed bg-gray-100 text-gray-400 shadow-none",
                  )}
                >
                  {isConnectionStepLoading ? (
                    <CircleNotch
                      size={18}
                      weight="bold"
                      className="animate-spin"
                    />
                  ) : null}
                  {t("website.onboarding.common.continue")}
                  {!isConnectionStepLoading ? (
                    <ArrowRight size={18} weight="bold" />
                  ) : null}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>


      <Modal
        isOpen={Boolean(editingLabelInstance)}
        onClose={() => setEditingLabelInstance(null)}
        title={t("website.integrationsSettings.editLabel")}
        width="max-w-md"
        headerClassName="pb-2 px-6 pt-5"
        bodyClassName="px-6 pb-6 pt-0"
        overlayClassName="z-[10000] bg-black/40 animate-in fade-in duration-200"
      >
        {editingLabelInstance ? (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[#1A162F]">
                {t("website.integrationsSettings.label")}
              </label>
              <input
                value={editingLabelValue}
                onChange={(e) => setEditingLabelValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!editingLabelInstance) return;
                    const inst = editingLabelInstance;
                    const val = editingLabelValue;
                    setEditingLabelInstance(null);
                    void handlePluginConnectionLabelSave(inst, val);
                  }
                }}
                placeholder={t("website.integrationsSettings.labelPlaceholder")}
                className="h-10 w-full rounded-xl border border-[#D9DCE6] bg-white px-3.5 text-sm text-[#1A162F] outline-none transition-colors placeholder:text-[#A1A8BA] focus:border-[#1A162F]"
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setEditingLabelInstance(null)}
                className="rounded-full px-4 py-2 text-sm font-semibold text-[#666F8D] hover:bg-[#F4F5F8] transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!editingLabelInstance) return;
                  const inst = editingLabelInstance;
                  const val = editingLabelValue;
                  setEditingLabelInstance(null);
                  void handlePluginConnectionLabelSave(inst, val);
                }}
                disabled={Boolean(savingLabelConnectionId)}
                className="flex items-center gap-1.5 rounded-full bg-[#1A162F] px-5 py-2 text-sm font-semibold text-white hover:bg-[#241F3D] transition-colors disabled:opacity-50"
              >
                {savingLabelConnectionId ? (
                  <CircleNotch size={16} weight="bold" className="animate-spin" />
                ) : null}
                {t("common.save")}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {connectionReview ? (
        <IntegrationDisclosureModal
          tool={connectionReview.platformId}
          isPlugin={isPluginOnboarding}
          onClose={() => setConnectionReview(null)}
          onContinue={() => {
            const request = connectionReview;
            setConnectionReview(null);
            void continueReviewedConnection(request.platformId, request.provider);
          }}
        />
      ) : null}

      <ShopifyConnectionEntry
        key={shopifyEntryAttempt}
        scene="onboarding"
        isOpen={!!connectingTool && !(connectingTool === "Shopify" && hasShopifyReturn)}
        platform={connectingTool}
        onClose={() => { resetConnectionForm(); setShopifyEntryState("idle"); }}
        onProgress={setShopifyEntryState}
        workspaceId={data.workspaceId || null}
      >
        <StoreConnectionModal
          isOpen={!!connectingTool && !!STORE_URL_INPUT_CONFIG[connectingTool]}
          platform={connectingTool}
          storeUrl={storeUrl}
          onStoreUrlChange={setStoreUrl}
          onClose={() => {
            resetConnectionForm();
          }}
          onSubmit={handleConnectionSubmit}
          submitLabel={t("website.onboarding.connect.connect")}
          isLoading={syncingTools.has(connectingTool || "")}
          submitDisabled={
            !storeUrl.trim() ||
            syncingTools.has(connectingTool || "") ||
            (connectingTool === "BigCommerce" &&
              (!bigCommerceStoreHash.trim() || !bigCommerceAccountUuid.trim()))
          }
          extraFields={[
            {
              key: "connection_alias",
              label: t("website.integrationsSettings.connectionLabel"),
              placeholder: getIntegrationConnectionLabelPlaceholder(
                t,
                connectingTool || "",
              ),
              value: storeConnectionLabel,
              onChange: setStoreConnectionLabel,
              placement: "before-store-url" as const,
              autoFocus: true,
            },
            ...(connectingTool === "BigCommerce"
              ? [
                  {
                    key: "store_hash",
                    label: t("website.onboarding.connect.storeHash"),
                    placeholder: t(
                      "website.onboarding.connect.storeHashPlaceholder",
                    ),
                    value: bigCommerceStoreHash,
                    onChange: setBigCommerceStoreHash,
                  },
                  {
                    key: "account_uuid",
                    label: t("website.onboarding.connect.accountUuid"),
                    placeholder: t(
                      "website.onboarding.connect.accountUuidPlaceholder",
                    ),
                    value: bigCommerceAccountUuid,
                    onChange: setBigCommerceAccountUuid,
                  },
                ]
              : []),
          ]}
        />
      </ShopifyConnectionEntry>

      <IntegrationApiKeyModal
        isOpen={!!manualCredentialPlatformId}
        onClose={() => {
          if (!isManualCredentialConnecting) {
            resetManualCredentialForm();
          }
        }}
        toolName={manualCredentialPlatformId || "PrestaShop"}
        apiKey={manualCredentialApiKey}
        onApiKeyChange={setManualCredentialApiKey}
        accountLabel={manualCredentialAccountLabel}
        onAccountLabelChange={setManualCredentialAccountLabel}
        storeUrl={manualCredentialStoreUrl}
        onStoreUrlChange={setManualCredentialStoreUrl}
        onConfirm={handleManualCredentialSubmit}
        isLoading={isManualCredentialConnecting}
      />

      <IntegrationCredentialsModal
        isOpen={!!credentialTool}
        onClose={() => {
          if (!isIntegrationCredentialConnecting) {
            resetIntegrationCredentialForm();
          }
        }}
        tool={credentialTool}
        values={credentialValues}
        onValuesChange={setCredentialValues}
        onConfirm={handleIntegrationCredentialSubmit}
        isLoading={isIntegrationCredentialConnecting}
      />

      <GoogleAdsAccountSelectionModal
        isOpen={Boolean(googleAdsAccountSelectionConnectionId)}
        connectionId={googleAdsAccountSelectionConnectionId}
        workspaceId={data.workspaceId}
        initialResponse={googleAdsAccountSelectionDiscovery?.response}
        initialResponseScope={
          googleAdsAccountSelectionDiscovery
            ? {
                workspaceId: googleAdsAccountSelectionDiscovery.workspaceId,
                connectionId: googleAdsAccountSelectionDiscovery.connectionId,
              }
            : null
        }
        brandOptions={googleAdsBrandOptions}
        showBusinessGroupAssignment={false}
        onClose={() => {
          void handleGoogleAdsAccountSelectionClose();
        }}
        onSaved={handleGoogleAdsAccountSelectionSaved}
        allowBusinessGroupAssignment={false}
      />

      {onboardingResourceSelection ? (
        <OnboardingResourceSelectionModal
          isOpen
          provider={onboardingResourceSelection.displayProvider}
          resources={onboardingResourceSelection.resources}
          isLoading={onboardingResourceSelection.isLoading}
          isSaving={isSavingOnboardingResources}
          onCancel={handleOnboardingResourceSelectionCancel}
          onSave={handleOnboardingResourceSelectionSaved}
        />
      ) : null}


    </div>
  );
};

const StepPlan: React.FC<{
  onBack?: () => void;
  data: any;
  updateData: (data: any) => void;
  onComplete?: () => void;
  isPluginOnboarding: boolean;
}> = ({ onBack, data, updateData, onComplete, isPluginOnboarding }) => {
  const { t, locale } = useTranslation();
  const {
    profile,
    setProfile,
    refreshWorkspaces,
    switchWorkspace,
    userBilling,
  } = useAuth();
  const { showToast } = useToast();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planComparison, setPlanComparison] = useState<{
    workspaceId?: string;
    catalog: PlanComparisonCatalogResponse;
  } | null>(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [planLoadError, setPlanLoadError] = useState(false);
  const [planLoadAttempt, setPlanLoadAttempt] = useState(0);
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [pendingPlanOption, setPendingPlanOption] =
    useState<OnboardingPlanOption | null>(null);
  const trialWorkUnitsLabel = useMemo(() => {
    const value = new Intl.NumberFormat(locale).format(
      resolveOnboardingTrialWorkUnits(plans),
    );
    return t("website.onboarding.plan.trialWorkUnits", { value: value });
  }, [locale, plans, t]);
  const pluginPlanButtonText =
    data.trialEligibilityState === "checkout_required"
      ? t("website.pricing.startSubscription")
      : t("website.onboarding.plan.pluginTrialCta");
  const planOptions = useMemo(
    () =>
      buildOnboardingPlanOptions(
        t,
        plans,
        locale,
        trialWorkUnitsLabel,
        isPluginOnboarding,
        pluginPlanButtonText,
        planLoadError,
        data.trialEligibilityState,
        planComparison?.workspaceId === data.workspaceId ? planComparison?.catalog : null,
      ),
    [
      isPluginOnboarding,
      data.trialEligibilityState,
      data.workspaceId,
      planComparison,
      locale,
      plans,
      planLoadError,
      pluginPlanButtonText,
      t,
      trialWorkUnitsLabel,
    ],
  );
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadPlans = async () => {
      setIsLoadingPlans(true);
      setPlanLoadError(false);
      setPlans([]);
      setPendingPlanOption(null);
      setLoadingPlanId(null);
      try {
        const response = isPluginOnboarding
          ? await billingService.getPluginPlans(
              data.workspaceId
                ? { headers: { "X-Workspace-Id": data.workspaceId } }
                : undefined,
            )
          : await billingService.getPlans(
              data.workspaceId
                ? { headers: { "X-Workspace-Id": data.workspaceId } }
                : undefined,
            );
        const activePlans = response.filter((plan) => plan.isActive);
        if (activePlans.length === 0) {
          throw new Error("No active onboarding plans returned");
        }
        if (!cancelled) {
          setPlans(activePlans);
        }
      } catch (error) {
        if (!cancelled) {
          setPlanLoadError(true);
          console.error("Failed to load onboarding plans", error);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingPlans(false);
        }
      }
    };

    void loadPlans();

    return () => {
      cancelled = true;
    };
  }, [data.workspaceId, isPluginOnboarding, planLoadAttempt]);

  // Core connection copy shares Admin's backend catalog; it does not own plan loading.
  useEffect(() => {
    let cancelled = false;
    setPlanComparison(null);
    if (isPluginOnboarding) return;

    const loadComparison = async () => {
      try {
        const catalog = await billingService.getPlanComparisonCatalog(
          data.workspaceId ? { headers: { "X-Workspace-Id": data.workspaceId } } : undefined,
        );
        if (!cancelled) setPlanComparison({ workspaceId: data.workspaceId, catalog });
      } catch (error) {
        if (!cancelled) console.error("Failed to load onboarding plan comparison catalog", error);
        // The existing shared catalog remains the fallback; plan selection can continue.
      }
    };
    void loadComparison();
    return () => { cancelled = true; };
  }, [data.workspaceId, isPluginOnboarding, planLoadAttempt]);

  const getMonthlyPlan = (planCode: string) =>
    plans.find(
      (plan) => plan.code === planCode && plan.billingCycle === "monthly",
    );

  const getAvailableMonthlyPlan = (planCode: string) =>
    isPluginOnboarding
      ? resolvePluginMonthlyPlan(plans, planCode)
      : getMonthlyPlan(planCode);

  const getPlanStoreAvailability = (planOption: OnboardingPlanOption) =>
    resolveOnboardingPlanAvailability(
      getAvailableMonthlyPlan(planOption.planCode),
      Number(data.connectedStoreCount || 0),
    );

  const startCheckout = async (
    targetPlan: Plan,
    options?: { allowTrial?: boolean },
  ): Promise<OnboardingCheckoutNavigationState> => {
    const { successUrl, cancelUrl } = buildCheckoutReturnUrls({
      currentUrl: window.location.href,
      mode: "onboarding",
      planId: targetPlan.id,
      planCode: targetPlan.code,
      planName: targetPlan.name,
      billingCycle: targetPlan.billingCycle,
      workspaceId: data.workspaceId,
    });

    if (isPluginOnboarding) {
      const checkoutResponse = await billingService.createPluginSubscription({
        planId: targetPlan.id,
        successUrl,
        cancelUrl,
        axiosRequestConfig: {
          headers: { "X-Workspace-Id": data.workspaceId },
        },
      });

      if (checkoutResponse?.paymentUrl) {
        window.location.href = checkoutResponse.paymentUrl;
        return "redirecting";
      }
    } else {
      const checkoutResponse = await billingService.createSubscription({
        planId: targetPlan.id,
        successUrl,
        cancelUrl,
        allowTrial: options?.allowTrial,
        axiosRequestConfig: { headers: { "X-Workspace-Id": data.workspaceId } },
      });

      if (checkoutResponse?.success && checkoutResponse?.paymentUrl) {
        window.location.href = checkoutResponse.paymentUrl;
        return "redirecting";
      }
    }

    showToast(t("website.onboarding.plan.failedToStartCheckout"), "error");
    return "settled";
  };

  const handleSelectPlan = async (planOption: OnboardingPlanOption) => {
    if (loadingPlanId && loadingPlanId !== planOption.id) {
      return;
    }

    if (planLoadError) {
      return;
    }

    if (!data.workspaceId) {
      showToast(t("website.onboarding.plan.workspaceNotReady"), "error");
      return;
    }

    if (isLoadingPlans) {
      setPendingPlanOption(planOption);
      setLoadingPlanId(planOption.id);
      return;
    }

    setPendingPlanOption(null);

    const targetPlan = getAvailableMonthlyPlan(planOption.planCode);
    if (!targetPlan) {
      setLoadingPlanId(null);
      showToast(
        t("website.onboarding.plan.unavailableNow", { plan: planOption.title }),
        "error",
      );
      return;
    }

    const planStoreAvailability = getPlanStoreAvailability(planOption);
    if (!planStoreAvailability.allowed) {
      showToast(
        t("website.onboarding.plan.storeLimitReached", {
          connected: Number(data.connectedStoreCount || 0),
          limit: planStoreAvailability.storeLimit,
        }),
        "error",
      );
      return;
    }

    updateData({ plan: planOption.id });

    let checkoutNavigationState: OnboardingCheckoutNavigationState = "settled";

    try {
      if (data.trialEligibilityState === "checkout_required") {
        setLoadingPlanId(planOption.id);
        checkoutNavigationState = await startCheckout(targetPlan, {
          allowTrial: false,
        });
        return;
      }

      setLoadingPlanId(planOption.id);

      const checkoutUrls = buildCheckoutReturnUrls({
        currentUrl: window.location.href,
        mode: "onboarding",
        planId: targetPlan.id,
        planCode: targetPlan.code,
        planName: targetPlan.name,
        billingCycle: targetPlan.billingCycle,
        workspaceId: data.workspaceId,
        onboardingTrialSetup: true,
      });

      const response = isPluginOnboarding
        ? await billingService.activatePluginTrialCheckout({
            workspaceId: data.workspaceId,
            planId: targetPlan.id,
            successUrl: checkoutUrls.successUrl,
            cancelUrl: checkoutUrls.cancelUrl,
            axiosRequestConfig: {
              headers: { "X-Workspace-Id": data.workspaceId },
            },
          })
        : await billingService.activateOnboardingTrial({
            planId: targetPlan.id,
            successUrl: checkoutUrls.successUrl,
            cancelUrl: checkoutUrls.cancelUrl,
            axiosRequestConfig: {
              headers: { "X-Workspace-Id": data.workspaceId },
            },
          });

      const activationAction = resolveOnboardingTrialActivationAction({
        isPluginOnboarding,
        response,
      });

      if (isPluginOnboarding) {
        console.info("[Onboarding] Plugin trial activation resolved", {
          workspaceId: response?.workspaceId || data.workspaceId,
          activationMode: response?.activationMode || null,
          action: activationAction,
          hasPaymentUrl: Boolean(response?.paymentUrl),
        });
      }

      if (activationAction === "redirect-to-card-setup") {
        window.location.href = response!.paymentUrl!;
        checkoutNavigationState = "redirecting";
        return;
      }

      if (activationAction === "start-paid-checkout") {
        checkoutNavigationState = await startCheckout(targetPlan, {
          allowTrial: false,
        });
        return;
      }

      if (activationAction === "activated" && response?.workspaceId) {
        const activatedWorkspaceId = response.workspaceId;

        if (profile) {
          setProfile(
            buildOnboardingTrialProfile({
              profile,
              planId: targetPlan.id,
              planCode: targetPlan.code,
              planName: targetPlan.name,
              billingCycle: targetPlan.billingCycle as "monthly" | "yearly",
            }),
          );
        }

        await Promise.all([
          userBilling.refreshAll(),
          refreshWorkspaces({
            silent: true,
            skipEmptyRedirect: true,
            preferredWorkspaceId: activatedWorkspaceId,
          }),
        ]);

        onComplete?.();
        navigate(
          buildOnboardingAdminSuccessRoute({
            workspaceId: activatedWorkspaceId,
            planCode: targetPlan.code,
            planName: targetPlan.name,
            billingCycle: targetPlan.billingCycle,
            onboardingTrialSetup: true,
          }),
          { replace: true },
        );
        return;
      }

      throw new Error(
        response?.message || t("website.onboarding.plan.failedToStartTrial"),
      );
    } catch (error: any) {
      showToast(
        error?.message || t("website.onboarding.plan.failedToStartTrial"),
        "error",
      );
    } finally {
      if (shouldReleaseOnboardingPlanLoading(checkoutNavigationState)) {
        setLoadingPlanId(null);
      }
    }
  };

  useEffect(() => {
    if (isLoadingPlans || !pendingPlanOption) {
      return;
    }

    void handleSelectPlan(pendingPlanOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPlans, pendingPlanOption, plans]);

  return (
    <div
      className={clsx(
        "mx-auto flex min-h-[calc(100vh-88px)] w-full flex-1 flex-col items-center justify-center px-6 py-12 lg:px-8",
        isPluginOnboarding ? "max-w-[1280px]" : "max-w-[1140px]",
      )}
    >
      <div className="fixed left-6 top-6 z-[101]">
        <ProductBrandLogo
          coreLogoSrc={wordmark}
          className={
            isPluginOnboarding
              ? "h-auto w-[112px] sm:w-[128px]"
              : "h-9 w-auto sm:h-10"
          }
        />
      </div>

      <div className="flex w-full flex-col items-center">
        <div className="flex w-full flex-col items-center">
          <div className="flex w-full max-w-[700px] items-center text-center mb-10 flex-col">
            <h2 className="text-[32px] font-medium leading-[1.1] text-[#1A162F] sm:text-[36px] lg:text-[40px]">
              {t(
                data.trialEligibilityState === "checkout_required"
                  ? isPluginOnboarding
                    ? "website.onboarding.plan.pluginPaidTitle"
                    : "website.onboarding.plan.paidTitle"
                  : "website.onboarding.plan.title",
              )}
            </h2>
          </div>

          <div
            className={clsx(
              "flex w-full flex-col items-center pt-0",
              isPluginOnboarding ? "max-w-[1140px]" : "max-w-[820px]",
            )}
          >
            {planLoadError ? (
              <div
                role="alert"
                className="mb-5 flex w-full items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              >
                <span>{t("website.onboarding.plan.loadFailed")}</span>
                <button
                  type="button"
                  className="shrink-0 font-semibold underline underline-offset-2"
                  onClick={() => setPlanLoadAttempt((attempt) => attempt + 1)}
                >
                  {t("website.onboarding.plan.retry")}
                </button>
              </div>
            ) : null}
            <div
              className={clsx(
                "grid w-full grid-cols-1 gap-5 text-left",
                isPluginOnboarding
                  ? "max-w-[1140px] items-stretch lg:grid-cols-2"
                  : "items-stretch lg:grid-cols-2",
              )}
            >
              {planOptions.map((plan) => {
                const planStoreAvailability = getPlanStoreAvailability(plan);
                const isUnavailable =
                  !isLoadingPlans &&
                  (planLoadError ||
                    !getAvailableMonthlyPlan(plan.planCode) ||
                    !planStoreAvailability.allowed);
                const { isSelectedPlanProcessing, isDisabled } =
                  resolveOnboardingPlanCardState({
                    loadingPlanId,
                    planId: plan.id,
                    isUnavailable,
                  });
                return (
                  <div
                    key={plan.id}
                    className="flex h-full min-w-0 w-full flex-col"
                  >
                    <PricingCard
                      plan={plan.pricingCard}
                      isOnboarding={true}
                      loadingPlanName={isSelectedPlanProcessing ? plan.pricingCard.name : undefined}
                      disabled={isDisabled}
                      onAction={() => {
                        void handleSelectPlan(plan);
                      }}
                    />
                    {!isLoadingPlans &&
                    !planStoreAvailability.allowed &&
                    planStoreAvailability.storeLimit !== null ? (
                      <p className="mt-2 text-center text-xs leading-relaxed text-amber-700">
                        {t("website.onboarding.plan.storeLimitReached", {
                          connected: Number(data.connectedStoreCount || 0),
                          limit: planStoreAvailability.storeLimit,
                        })}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <p className="mt-3 w-full text-center text-[12px] leading-relaxed text-[#666F8D]">
              {t("website.onboarding.plan.renewalNote")}
            </p>

            <div className="mt-10 flex w-full flex-wrap items-center justify-start gap-3">
              <button
                onClick={onBack}
                className="flex min-w-[116px] items-center justify-center rounded-full border border-gray-200 bg-transparent px-7 py-3.5 text-[15px] font-bold text-[#1A162F] shadow-sm transition-all hover:border-gray-300 hover:bg-white hover:shadow"
              >
                {t("website.onboarding.common.back")}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

const OnboardingOverlay: React.FC<{ invitationsVerified?: boolean }> = ({ invitationsVerified = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    nextStep,
    prevStep,
    stopOnboarding,
    onboardingData,
    updateOnboardingData,
    isAcceptingInvite,
    setIsAcceptingInvite,
  } = useOnboarding();
  const {
    user,
    profile,
    workspaceMembers,
    logout,
    workspaces,
    isLoadingWorkspaces,
    refreshWorkspaces,
  } = useAuth();
  const { invitations, isLoadingInvitations } =
    workspaceMembers;
  const currentStep = onboardingData.currentStep ?? 0;
  const productCode = getCurrentProductCode();
  const isPluginOnboarding = isPluginProduct(productCode);
  const requiredConnectionCount = isPluginOnboarding
    ? PLUGIN_ONBOARDING_MIN_CONNECTION_COUNT
    : BUSTLY_CORE_ONBOARDING_MIN_CONNECTION_COUNT;
  const connectionRequirementBasis: OnboardingConnectionRequirementBasis =
    "connection_instances";
  const workspaceIntegrations = useWorkspaceIntegrations(
    onboardingData.workspaceId,
    {
      enabled: Boolean(onboardingData.workspaceId),
      requireFreshFetch: true,
    },
  );
  const [userChoseCreate, setUserChoseCreate] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<InviteStatusResponse | null>(
    null,
  );
  const [isLoadingInviteStatus, setIsLoadingInviteStatus] = useState(false);
  const [inviteStatusError, setInviteStatusError] = useState<string | null>(
    null,
  );
  const [displayStep, setDisplayStep] = useState(currentStep);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPreparingWorkspace, setIsPreparingWorkspace] = useState(false);
  const [workspacePreparationError, setPrepareWorkspaceError] = useState<
    WorkspacePreparationError | null
  >(null);
  const prepareWorkspaceError = formatWorkspacePreparationError(workspacePreparationError, t);
  const [isFinalizingWorkspace, setIsFinalizingWorkspace] = useState(false);
  const [finalizeWorkspaceError, setFinalizeWorkspaceError] = useState<
    string | null
  >(null);
  const [pendingIntentSubmission, setPendingIntentSubmission] = useState<{
    intent?: string;
    otherIntent?: string;
  } | null>(null);
  const onboardingScrollContainerRef = useRef<HTMLDivElement>(null);
  const intentSubmissionLockRef = useRef(false);
  const workspacePreparationLockRef = useRef(false);
  const workspacePreparationIdRef = useRef<string | null>(null);
  const autoAcceptInviteKeyRef = useRef<string | null>(null);
  const acceptedInviteWorkspaceKeyRef = useRef<string | null>(null);
  const inviteToken = searchParams.get("invite_token")?.trim() || "";
  const currentInviteEmails = useMemo(
    () =>
      resolveCurrentInviteEmails(
        user?.email,
        profile?.userInfo?.email,
        user?.identities,
      ),
    [profile?.userInfo?.email, user?.email, user?.identities],
  );
  const normalizedCurrentEmail =
    resolveCurrentInviteEmail(user?.email, profile?.userInfo?.email) ||
    currentInviteEmails[0] ||
    "";
  const currentInviteEmailKey = currentInviteEmails.join("|");
  const shouldSkipStoreConnectStep = false;
  const visibleOnboardingSteps = useMemo(
    () => getVisibleOnboardingSteps(shouldSkipStoreConnectStep),
    [shouldSkipStoreConnectStep],
  );
  const hasFreshConnectionSnapshot =
    workspaceIntegrations.hasFetchedCurrentWorkspace &&
    !workspaceIntegrations.error;
  const planEntryConnectionRequirements = useMemo(
    () =>
      resolveOnboardingConnectionRequirements({
        serverIntegrations: workspaceIntegrations.allIntegrations,
        persistedConnectedToolNames: onboardingData.connectedIntegrations || [],
        serverHydrated:
          Boolean(onboardingData.workspaceId) && hasFreshConnectionSnapshot,
        requiredConnectionCount,
        connectionRequirementBasis,
        requiresStoreConnection: false,
        excludedCategoryIds: isPluginOnboarding
          ? PLUGIN_ONBOARDING_EXCLUDED_CATEGORY_IDS
          : [],
        connectionCategories: getOnboardingConnectionCategories(
          isPluginOnboarding,
        ),
      }),
    [
      hasFreshConnectionSnapshot,
      onboardingData.connectedIntegrations,
      onboardingData.workspaceId,
      connectionRequirementBasis,
      isPluginOnboarding,
      requiredConnectionCount,
      workspaceIntegrations.allIntegrations,
    ],
  );
  const planStepAccessState = resolveOnboardingPlanStepAccess({
    requirements: planEntryConnectionRequirements,
    hasFreshServerSnapshot: hasFreshConnectionSnapshot,
    shouldSkipConnectionStep: shouldSkipStoreConnectStep,
  });
  const shouldRestorePlanConnectionState =
    currentStep === 2
    && !shouldSkipStoreConnectStep
    && !onboardingData.resumePlanAfterCheckout;
  const hasPlanStepRestoreError =
    shouldRestorePlanConnectionState && Boolean(workspaceIntegrations.error);
  const isRestoringPlanStep =
    shouldRestorePlanConnectionState
    && planStepAccessState === "checking-connections"
    && !hasPlanStepRestoreError;
  const shouldShowConnectStepForPlanAccess =
    currentStep === 2
    && planStepAccessState === "blocked"
    && !onboardingData.resumePlanAfterCheckout;
  const stepForRendering = resolveOnboardingStepForRendering({
    displayStep,
    planStepAccessState,
    preservePlanStep: Boolean(onboardingData.resumePlanAfterCheckout),
  });
  const guardedCurrentStep = shouldShowConnectStepForPlanAccess
    ? 1
    : currentStep;
  const visibleCurrentStepIndex = getVisibleOnboardingStepIndex(
    guardedCurrentStep,
    shouldSkipStoreConnectStep,
  );

  useWorkspacePolling();

  useEffect(() => {
    if (
      currentStep !== 2
      || planStepAccessState !== "blocked"
      || onboardingData.resumePlanAfterCheckout
    ) {
      return;
    }

    updateOnboardingData({ currentStep: 1 });
  }, [
    currentStep,
    onboardingData.resumePlanAfterCheckout,
    planStepAccessState,
    updateOnboardingData,
  ]);

  const isNewUser = !workspaces || workspaces.length === 0;
  useEffect(() => {
    let isCancelled = false;

    if (!inviteToken) {
      setInviteStatus(null);
      setInviteStatusError(null);
      setIsLoadingInviteStatus(false);
      return;
    }

    const loadInviteStatus = async () => {
      setIsLoadingInviteStatus(true);
      setInviteStatusError(null);

      try {
        const response = await featureService.getInviteStatus(inviteToken);

        if (!isCancelled) {
          setInviteStatus(response);
        }
      } catch (error: any) {
        if (!isCancelled) {
          setInviteStatus(null);
          setInviteStatusError(
            error?.message || "We could not load this invitation.",
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingInviteStatus(false);
        }
      }
    };

    void loadInviteStatus();

    return () => {
      isCancelled = true;
    };
  }, [inviteToken]);


  useEffect(() => {
    if (currentStep !== displayStep) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayStep(currentStep);
        if (onboardingScrollContainerRef.current) {
          onboardingScrollContainerRef.current.scrollTop = 0;
        }
        requestAnimationFrame(() => {
          setIsTransitioning(false);
        });
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentStep, displayStep]);

  const targetedInvitation = useMemo<WorkspaceInvitation | null>(() => {
    if (!inviteToken || !inviteStatus?.valid) {
      return null;
    }

    const matchedInvitation =
      invitations.find((invitation) => invitation.token === inviteToken) ||
      invitations.find(
        (invitation) =>
          invitation.workspaceId === inviteStatus.workspaceId &&
          normalizeEmail(invitation.email) ===
            normalizeEmail(inviteStatus.invitedEmail),
      );

    if (matchedInvitation) {
      return matchedInvitation;
    }

    return {
      id: `invite-token:${inviteToken}`,
      workspaceId: inviteStatus.workspaceId,
      workspaceName: inviteStatus.workspaceName,
      email: inviteStatus.invitedEmail,
      role: inviteStatus.role,
      dataRoleName:
        inviteStatus.dataRoleName ||
        inviteStatus.data_role_name ||
        inviteStatus.workspaceRoleName ||
        inviteStatus.workspaceRoleKey ||
        null,
      status: "PENDING",
      invitedBy:
        inviteStatus.inviterName || inviteStatus.inviterEmail || "A teammate",
      inviterEmail: inviteStatus.inviterEmail,
      inviterName: inviteStatus.inviterName,
      token: inviteToken,
      expiresAt: inviteStatus.expiresAt,
      createdAt: new Date().toISOString(),
    } as unknown as WorkspaceInvitation;
  }, [inviteStatus, inviteToken, invitations]);

  const hasInviteEmailMismatch = Boolean(
    inviteToken &&
    inviteStatus?.valid &&
    inviteStatus.invitedEmail &&
    currentInviteEmails.length > 0 &&
    !inviteEmailMatchesCurrentUser(
      inviteStatus.invitedEmail,
      normalizedCurrentEmail,
      currentInviteEmails,
    ),
  );
  const inviteEmailMatchesUser = !hasInviteEmailMismatch;
  const shouldProcessInviteToken = shouldProcessInviteTokenOnOnboarding({
    inviteToken,
    inviteValid: inviteStatus?.valid,
    inviteStatus: inviteStatus?.status,
    invitedEmail: inviteStatus?.invitedEmail,
    currentEmail: normalizedCurrentEmail,
    currentEmails: currentInviteEmails,
    userChoseCreate,
    hasInviteStatusError: Boolean(inviteStatusError),
  });
  const showTargetedInviteSelection = Boolean(
    inviteToken &&
    inviteStatus?.valid &&
    inviteEmailMatchesUser &&
    !userChoseCreate &&
    !shouldProcessInviteToken &&
    targetedInvitation,
  );

  useEffect(() => {
    const shouldAutoAccept = shouldAutoAcceptInviteOnOnboarding({
      inviteToken,
      inviteValid: inviteStatus?.valid,
      inviteStatus: inviteStatus?.status,
      invitedEmail: inviteStatus?.invitedEmail,
      currentEmail: normalizedCurrentEmail,
      currentEmails: currentInviteEmails,
      isAcceptingInvite,
      userChoseCreate,
    });

    if (!shouldAutoAccept) {
      return undefined;
    }

    const autoAcceptKey = `${inviteToken}:${currentInviteEmailKey}`;
    if (autoAcceptInviteKeyRef.current === autoAcceptKey) {
      return undefined;
    }

    autoAcceptInviteKeyRef.current = autoAcceptKey;
    let isCancelled = false;

    const acceptInviteAfterAuthCallback = async () => {
      setIsAcceptingInvite(true);
      setInviteStatusError(null);

      try {
        const response = await featureService.acceptInvitation(inviteToken);
        const workspaceId = resolveInviteAcceptWorkspaceId(response);

        if (!workspaceId) {
          throw new Error("We could not accept this invitation.");
        }

        if (isCancelled) {
          return;
        }

        stopOnboarding();
        window.location.assign(buildInviteSuccessAdminRoute(workspaceId, window.location.search));
      } catch (error: any) {
        if (!isCancelled) {
          setInviteStatusError(
            error?.message || "We could not accept this invitation.",
          );
          setIsAcceptingInvite(false);
        }
      }
    };

    void acceptInviteAfterAuthCallback();

    return () => {
      isCancelled = true;
    };
  }, [
    inviteStatus?.invitedEmail,
    inviteStatus?.valid,
    inviteToken,
    currentInviteEmailKey,
    currentInviteEmails,
    normalizedCurrentEmail,
    setIsAcceptingInvite,
    stopOnboarding,
    userChoseCreate,
  ]);

  useEffect(() => {
    const workspaceId = inviteStatus?.workspaceId;
    const shouldEnterAcceptedWorkspace =
      shouldEnterAcceptedInviteWorkspaceOnOnboarding({
        inviteToken,
        inviteValid: inviteStatus?.valid,
        inviteStatus: inviteStatus?.status,
        workspaceId,
        invitedEmail: inviteStatus?.invitedEmail,
        currentEmail: normalizedCurrentEmail,
        currentEmails: currentInviteEmails,
        isAcceptingInvite,
        userChoseCreate,
      });

    if (!shouldEnterAcceptedWorkspace || !workspaceId) {
      return undefined;
    }

    const acceptedInviteKey = `${inviteToken}:${workspaceId}:${currentInviteEmailKey}`;
    if (acceptedInviteWorkspaceKeyRef.current === acceptedInviteKey) {
      return undefined;
    }

    acceptedInviteWorkspaceKeyRef.current = acceptedInviteKey;
    let isCancelled = false;

    const enterAcceptedInviteWorkspace = async () => {
      setIsAcceptingInvite(true);
      setInviteStatusError(null);

      try {
        if (isCancelled) {
          return;
        }

        stopOnboarding();
        window.location.assign(buildInviteSuccessAdminRoute(workspaceId, window.location.search));
      } catch (error: any) {
        if (!isCancelled) {
          setInviteStatusError(
            error?.message || "We could not open this workspace.",
          );
          setIsAcceptingInvite(false);
        }
      }
    };

    void enterAcceptedInviteWorkspace();

    return () => {
      isCancelled = true;
    };
  }, [
    inviteStatus?.invitedEmail,
    inviteStatus?.status,
    inviteStatus?.valid,
    inviteStatus?.workspaceId,
    inviteToken,
    currentInviteEmailKey,
    currentInviteEmails,
    normalizedCurrentEmail,
    setIsAcceptingInvite,
    stopOnboarding,
    userChoseCreate,
  ]);
  const pendingConnectionRequest =
    onboardingData.pendingConnectionRequest ?? null;
  const setPendingConnectionRequest = useCallback<
    React.Dispatch<React.SetStateAction<PendingConnectionRequest | null>>
  >(
    (nextPendingConnectionRequest) => {
      const resolvedPendingConnectionRequest =
        typeof nextPendingConnectionRequest === "function"
          ? nextPendingConnectionRequest(pendingConnectionRequest)
          : nextPendingConnectionRequest;

      updateOnboardingData({
        pendingConnectionRequest: resolvedPendingConnectionRequest,
      });
    },
    [pendingConnectionRequest, updateOnboardingData],
  );
  const isCreatingOrConnectingWorkspace = Boolean(
    userChoseCreate || pendingConnectionRequest,
  );
  const showDefaultInviteSelection =
    invitationsVerified &&
    !isLoadingInvitations &&
    !isCreatingOrConnectingWorkspace &&
    !inviteToken &&
    invitations &&
    invitations.length > 0 &&
    !userChoseCreate;
  const showInviteSelection =
    showTargetedInviteSelection || showDefaultInviteSelection;
  const isWorkspaceReadyForConnect = Boolean(
    onboardingData.workspaceId &&
    !isPreparingWorkspace &&
    !prepareWorkspaceError,
  );

  const prepareWorkspaceForConnectStep = useCallback(async (
    trigger: "auto_step_effect" | "welcome_continue",
  ) => {
    const preparationId = onboardingData.creationRequestId
      || workspacePreparationIdRef.current
      || createOperationId();
    workspacePreparationIdRef.current = preparationId;
    const tracking = {
      area: "onboarding",
      action: "prepare_workspace",
      request_attempt_id: createOperationId(),
      product_code: getCurrentProductCode(),
      current_step: currentStep,
      trigger,
      creation_intent: "ensure",
      creation_source: onboardingData.creationSource || "automatic_onboarding",
      creation_request_id: onboardingData.creationRequestId,
      preparation_id: preparationId,
      known_workspace_id: onboardingData.workspaceId,
      workspace_count: workspaces?.length || 0,
      workspaces_loading: Boolean(isLoadingWorkspaces),
    };
    logSentryEvent("onboarding.workspace.prepare_triggered", tracking);
    if (
      hasReusableOnboardingWorkspace(onboardingData.workspaceId, workspaces)
    ) {
      logSentryEvent("onboarding.workspace.prepare_skipped", {
        ...tracking,
        skip_reason: "reusable_workspace",
      });
      return (
        workspaces?.find(
          (workspace) => workspace?.id === onboardingData.workspaceId,
        ) || null
      );
    }

    if (isPreparingWorkspace || workspacePreparationLockRef.current) {
      logSentryEvent("onboarding.workspace.prepare_skipped", {
        ...tracking,
        skip_reason: isPreparingWorkspace
          ? "state_in_flight"
          : "ref_locked",
      });
      return null;
    }

    workspacePreparationLockRef.current = true;
    setIsPreparingWorkspace(true);
    setPrepareWorkspaceError(null);

    const requestStrategy = `${isPluginOnboarding ? "plugin" : "core"}_ensure`;
    let requestStartedAt: number | null = null;
    let requestSucceeded = false;
    try {
      if (onboardingData.workspaceId) {
        updateOnboardingData({
          workspaceId: undefined,
          businessName: "",
        });
      }

      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const referralAttribution = !isPluginOnboarding && onboardingData.referralCode && onboardingData.referralEntryId && typeof window !== "undefined"
        ? readStoredReferralAttribution(window.localStorage, onboardingData.referralEntryId)
        : null;
      if (!isPluginOnboarding && onboardingData.referralCode
          && (referralAttribution?.code !== onboardingData.referralCode || !referralAttribution?.attributionToken)) {
        throw new Error(t('website.billing.referralAttributionExpired'));
      }
      const creationRequestId = onboardingData.creationRequestId || `workspace-create-${crypto.randomUUID()}`;
      tracking.creation_request_id = creationRequestId;
      if (!onboardingData.creationRequestId) {
        updateOnboardingData({ creationRequestId });
      }
      const workspacePayload = buildInitialOnboardingWorkspacePayload({
        timezone,
        referralCode: referralAttribution?.code,
        referralAttributionToken: referralAttribution?.attributionToken,
      });
      const ensureWorkspaceRequestConfig = { headers: { "Idempotency-Key": creationRequestId } };
      requestStartedAt = Date.now();
      const requestStartedTracking = {
        ...tracking,
        request_strategy: requestStrategy,
      };
      logSentryEvent(
        "onboarding.workspace.request_started",
        requestStartedTracking,
      );
      captureSentryDiagnosticEvent(
        "onboarding.workspace.request_started",
        requestStartedTracking,
      );
      const workspace = isPluginOnboarding
        ? await featureService.ensurePluginOnboardingWorkspace(workspacePayload, creationRequestId)
        : await featureService.ensureOnboardingWorkspace(workspacePayload, ensureWorkspaceRequestConfig);

      requestSucceeded = true;
      const requestSucceededTracking = {
        ...tracking,
        request_strategy: requestStrategy,
        duration_ms: Date.now() - requestStartedAt,
        returned_workspace_id: workspace.id,
        returned_workspace_status: workspace.status,
      };
      logSentryEvent(
        "onboarding.workspace.request_succeeded",
        requestSucceededTracking,
      );
      captureSentryDiagnosticEvent(
        "onboarding.workspace.request_succeeded",
        requestSucceededTracking,
      );
      // Keep this entry's token for idempotent preparation retries until its TTL.
      // The freshly created workspace may not yet appear in the profile workspace list.
      updateOnboardingData({
        workspaceId: workspace.id,
        businessName: workspace.name,
        logoUrl: workspace.logoUrl || null,
        connectedStoreName: "",
        connectedStoreLogoUrl: null,
        connectedIntegrations: [],
        connectedIntegrationTrialRequirements: {},
        trialEligibilityState: null,
        workspaceSettings: workspace.settings || null,
      });
      return workspace;
    } catch (error: any) {
      if (requestStartedAt !== null && !requestSucceeded) {
        const requestFailedTracking = {
          ...tracking,
          request_strategy: requestStrategy,
          duration_ms: Date.now() - requestStartedAt,
          error_code: getTrackingErrorCode(error),
        };
        logSentryEvent(
          "onboarding.workspace.request_failed",
          requestFailedTracking,
          "warn",
        );
        captureSentryDiagnosticEvent(
          "onboarding.workspace.request_failed",
          requestFailedTracking,
          "warn",
        );
      } else if (requestStartedAt !== null) {
        const clientStateFailedTracking = {
          ...tracking,
          request_strategy: requestStrategy,
          client_phase: "persist_created_workspace",
          duration_ms: Date.now() - requestStartedAt,
          error_code: getTrackingErrorCode(error),
        };
        logSentryEvent(
          "onboarding.workspace.client_state_failed",
          clientStateFailedTracking,
          "warn",
        );
        captureSentryDiagnosticEvent(
          "onboarding.workspace.client_state_failed",
          clientStateFailedTracking,
          "warn",
        );
      } else {
        const clientStateFailedTracking = {
          ...tracking,
          request_strategy: requestStrategy,
          client_phase: "clear_stale_workspace",
          error_code: getTrackingErrorCode(error),
        };
        logSentryEvent(
          "onboarding.workspace.client_state_failed",
          clientStateFailedTracking,
          "warn",
        );
        captureSentryDiagnosticEvent(
          "onboarding.workspace.client_state_failed",
          clientStateFailedTracking,
          "warn",
        );
      }
      if (isPluginOnboarding && isPluginCapacityFullError(error)) {
        navigate(PLUGIN_ACCESS_LIMITED_ROUTE, { replace: true });
        return null;
      }
      setPrepareWorkspaceError(
        resolveWorkspacePreparationError(error),
      );
      return null;
    } finally {
      workspacePreparationLockRef.current = false;
      setIsPreparingWorkspace(false);
    }
  }, [
    isPreparingWorkspace,
    isPluginOnboarding,
    navigate,
    onboardingData.workspaceId,
    onboardingData.creationRequestId,
    onboardingData.creationSource,
    currentStep,
    isLoadingWorkspaces,
    updateOnboardingData,
    workspaces,
  ]);

  useEffect(() => {
    if (!inviteToken && !invitationsVerified) {
      return;
    }
    if (currentStep < 1 || showInviteSelection || shouldProcessInviteToken) {
      return;
    }

    if (
      hasReusableOnboardingWorkspace(onboardingData.workspaceId, workspaces)
    ) {
      return;
    }

    if (
      onboardingData.workspaceId ||
      isPreparingWorkspace ||
      prepareWorkspaceError
    ) {
      return;
    }

    void prepareWorkspaceForConnectStep("auto_step_effect");
  }, [
    inviteToken,
    invitationsVerified,
    currentStep,
    onboardingData.workspaceId,
    isPreparingWorkspace,
    prepareWorkspaceError,
    prepareWorkspaceForConnectStep,
    shouldProcessInviteToken,
    showInviteSelection,
    workspaces,
  ]);

  const prepareWorkspaceOnWelcome = async () => {
    setUserChoseCreate(true);
    setPrepareWorkspaceError(null);

    const workspace = await prepareWorkspaceForConnectStep("welcome_continue");
    if (!workspace) {
      return;
    }

    try {
      if (await completeShopifyInstallWorkspaceHandoff(
        `?${searchParams.toString()}`, workspace.id, refreshWorkspaces, navigate,
      )) {
        updateOnboardingData({ currentStep: 1 });
        return;
      }
    } catch {
      setPrepareWorkspaceError({kind: 'other', message: t('website.shopifyInstall.workspaceError')});
      return;
    }
    nextStep();
  };

  const goBackFromPlanStep = useCallback(() => {
    if (shouldSkipStoreConnectStep) {
      updateOnboardingData({ currentStep: 0 });
      return;
    }

    prevStep();
  }, [prevStep, shouldSkipStoreConnectStep, updateOnboardingData]);

  const handleConnectNext = useCallback(async () => {
    if (intentSubmissionLockRef.current) {
      return;
    }

    intentSubmissionLockRef.current = true;
    setIsFinalizingWorkspace(true);
    setFinalizeWorkspaceError(null);

    try {
      const payload = buildOnboardingWorkspaceUpdatePayload({
        storeName:
          onboardingData.connectedStoreName || onboardingData.businessName,
        storeLogoUrl:
          onboardingData.connectedStoreLogoUrl ||
          onboardingData.logoUrl ||
          null,
        currentSettings: onboardingData.workspaceSettings,
        intent: "",
        otherIntent: "",
      });
      const workspaceUpdateRequestConfig = isPluginOnboarding
        ? withPluginProductHeaders({
            headers: { "X-Workspace-Id": onboardingData.workspaceId! },
          })
        : undefined;
      const workspace = await featureService.updateWorkspace(
        onboardingData.workspaceId!,
        payload,
        workspaceUpdateRequestConfig,
      );

      updateOnboardingData({
        businessName: workspace.name,
        logoUrl: workspace.logoUrl || null,
        workspaceSettings: workspace.settings || null,
      });

      nextStep();
    } catch (error: any) {
      if (currentStep === 2) {
        updateOnboardingData({ currentStep: 1 });
      }
      setFinalizeWorkspaceError(
        error?.message || "We could not finish setting up your workspace.",
      );
    } finally {
      intentSubmissionLockRef.current = false;
      setIsFinalizingWorkspace(false);
    }
  }, [
    currentStep,
    nextStep,
    onboardingData.businessName,
    onboardingData.connectedStoreLogoUrl,
    onboardingData.connectedStoreName,
    onboardingData.logoUrl,
    onboardingData.workspaceId,
    onboardingData.workspaceSettings,
    updateOnboardingData,
  ]);

  const handleComplete = () => {
    stopOnboarding();
  };

  const steps = [
    <StepWelcome
      key="welcome"
      onNext={prepareWorkspaceOnWelcome}
      error={prepareWorkspaceError}
      isLoading={isPreparingWorkspace}
      isPluginOnboarding={isPluginOnboarding}
    />,
    <StepConnect
      key="connect"
      onNext={handleConnectNext}
      onBack={prevStep}
      isLoading={isFinalizingWorkspace}
      data={onboardingData}
      updateData={updateOnboardingData}
      pendingConnectionRequest={pendingConnectionRequest}
      setPendingConnectionRequest={setPendingConnectionRequest}
      workspaceReady={isWorkspaceReadyForConnect}
      workspaceError={prepareWorkspaceError || finalizeWorkspaceError}
      workspaceIntegrations={workspaceIntegrations}
      requiredConnectionCount={requiredConnectionCount}
      connectionRequirementBasis={connectionRequirementBasis}
      isPluginOnboarding={isPluginOnboarding}
    />,
    <StepPlan
      key="plan"
      onBack={goBackFromPlanStep}
      data={onboardingData}
      updateData={updateOnboardingData}
      onComplete={handleComplete}
      isPluginOnboarding={isPluginOnboarding}
    />,
  ];
  return (
    <div
      ref={onboardingScrollContainerRef}
      className={clsx(
        "fixed inset-0 z-[100] overflow-x-hidden overflow-y-auto transition-colors duration-500",
        "bg-[#F8F9FB]",
      )}
    >
      {!showInviteSelection && !shouldProcessInviteToken ? (
        <div className="fixed top-0 left-0 w-full h-1 bg-gray-100 z-[101] transition-opacity duration-300">
          <div
            className="h-full bg-text-main transition-all duration-500 ease-out"
            style={{
              width: `${((visibleCurrentStepIndex + 1) / visibleOnboardingSteps.length) * 100}%`,
            }}
          />
        </div>
      ) : null}

          <div className="fixed top-6 right-6 z-[101] flex items-center gap-3">
          {isNewUser ? (
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 transition-all"
            >
              <SignOut size={16} className="text-[#1A162F]" weight="bold" />
              <span className="text-[13px] font-bold text-[#1A162F]">
                {t("website.onboarding.common.logOut")}
              </span>
            </button>
          ) : (
            <button
              onClick={() => {
                stopOnboarding();
                navigate("/");
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 transition-all"
            >
              <X size={16} className="text-[#1A162F]" weight="bold" />
              <span className="text-[13px] font-bold text-[#1A162F]">
                {t("website.onboarding.common.close")}
              </span>
            </button>
          )}
          </div>

      <div
        className={clsx(
          "min-h-screen flex flex-col pb-12 pt-6 transition-all duration-500 ease-out",
        )}
      >
        <div className="relative flex flex-1 w-full flex-col items-center justify-center">
          {isLoadingInviteStatus ? (
            <div className="flex flex-col items-center gap-3 text-text-sub">
              <CircleNotch
                size={28}
                weight="bold"
                className="animate-spin text-primary"
              />
              <p className="text-sm">
                {t("website.onboarding.invite.checkingInvitation")}
              </p>
            </div>
          ) : inviteToken && (!inviteStatus?.valid || inviteStatusError) ? (
            <InviteStateCard
              icon={<WarningCircle size={24} weight="bold" />}
              title={t("website.onboarding.invite.invalidTitle")}
              description={
                <>
                  <p>
                    {inviteStatusError ||
                      t("website.onboarding.invite.invalidDescription")}
                  </p>
                  {inviteStatus ? (
                    <p>{buildExpiryText(inviteStatus.daysUntilExpiry, t)}</p>
                  ) : null}
                </>
              }
              action={
                <button
                  onClick={() => navigate("/")}
                  className="rounded-xl bg-text-main px-6 py-3 font-semibold text-white transition-colors hover:bg-text-main/90"
                >
                  {t("website.onboarding.invite.goToDashboard")}
                </button>
              }
            />
          ) : shouldProcessInviteToken ? (
            <div className="flex flex-col items-center gap-3 text-text-sub">
              <CircleNotch
                size={28}
                weight="bold"
                className="animate-spin text-primary"
              />
              <p className="text-sm">
                {t("website.onboarding.invite.joining")}
              </p>
            </div>
          ) : inviteToken && inviteStatus?.valid && !inviteEmailMatchesUser ? (
            <InviteDifferentAccountCard
              inviteStatus={inviteStatus}
              currentEmail={normalizedCurrentEmail || null}
              onSwitchAccount={logout}
            />
          ) : showTargetedInviteSelection && targetedInvitation ? (
            <InviteSelection
              invitations={[targetedInvitation]}
              onChooseCreate={() => setUserChoseCreate(true)}
              onAcceptingChange={setIsAcceptingInvite}
            />
          ) : showDefaultInviteSelection ? (
            <InviteSelection
              invitations={invitations}
              onChooseCreate={() => setUserChoseCreate(true)}
              onAcceptingChange={setIsAcceptingInvite}
            />
          ) : hasPlanStepRestoreError ? (
            <div
              role="alert"
              className="flex max-w-[560px] flex-col items-center gap-4 rounded-2xl border border-red-200 bg-red-50 px-6 py-5 text-center text-[14px] text-red-700"
            >
              <span>{t("website.onboarding.connect.statusLoadFailed")}</span>
              <button
                type="button"
                onClick={() => {
                  void workspaceIntegrations.refresh();
                }}
                className="rounded-full border border-red-200 bg-white px-4 py-2 text-[13px] font-bold text-red-700 transition-colors hover:bg-red-100"
              >
                {t("common.retry")}
              </button>
            </div>
          ) : isRestoringPlanStep ? (
            <div
              className="flex flex-col items-center gap-3 text-text-sub"
              role="status"
              aria-live="polite"
            >
              <CircleNotch
                size={28}
                weight="bold"
                className="animate-spin text-primary"
              />
              <p className="text-sm">
                {t("website.onboarding.plan.loadingOptions")}
              </p>
            </div>
          ) : (
            <div
              className={clsx(
                "flex-1 flex flex-col w-full transition-all duration-300 ease-in-out will-change-[opacity,transform]",
                isTransitioning
                  ? "opacity-0 translate-y-4"
                  : "opacity-100 translate-y-0",
              )}
            >
              {steps[stepForRendering]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingOverlay;
